import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import config from '../config';
import useRingtone from '../hooks/useRingtone';
import '../styles/VideoCall.css';
import EndCallIcon from './icons/EndCallIcon';
import MuteIcon from './icons/MuteIcon';
import { leaveOtherCallSessions, registerCallSession } from '../utils/groupCallSession';
import {
  CALL_RECONNECT_GRACE_MS,
  clearActiveCall,
  consumeActiveCall,
  isPageUnloading,
  readActiveCall,
  saveActiveCall,
  waitForSocket,
} from '../utils/activeCallPersistence';

const MAX_CALL_PARTICIPANTS = 5;

const VideoCall = ({ user, selectedUser, onEndCall, socket }) => {
  const [isCallActive, setIsCallActive] = useState(false);
  const [isIncomingCall, setIsIncomingCall] = useState(false);
  const [callerId, setCallerId] = useState(null);
  const [callerName, setCallerName] = useState(null);
  const [remoteName, setRemoteName] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [callStatus, _setCallStatus] = useState('idle'); // idle, calling, incoming, connected, ended
  // Wrapper that keeps both state and ref in sync so socket handlers never see stale values.
  const setCallStatus = (s) => { callStatusRef.current = s; _setCallStatus(s); };
  const peerConnectionRef = useRef(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [error, setError] = useState(null);
  const [showAddParticipants, setShowAddParticipants] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [participantSearchQuery, setParticipantSearchQuery] = useState('');
  const callIdRef = useRef(null);
  const additionalPeerConnectionsRef = useRef(new Map()); // Map<userId, RTCPeerConnection>
  const [allParticipants, setAllParticipants] = useState(new Set()); // Track all participants in the call
  const allParticipantsRef = useRef(new Set());
  const sendingResumeOffersRef = useRef(false);
  const resumeAttemptedRef = useRef(false);
  const hadLiveCallRef = useRef(false);
  const buildVideoSnapshotRef = useRef(() => null);
  const remoteStreamsRef = useRef(new Map()); // Map<userId, MediaStream> - Store streams from each participant
  // React-rendered list of remote streams (so caller can SEE all participants)..
  const [remoteParticipantStreams, setRemoteParticipantStreams] = useState([]); // Array<{ userId: string, stream: MediaStream }>
  const [participantNames, setParticipantNames] = useState({}); // Map<userId, username>

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteTileVideoElsRef = useRef(new Map()); // Map<userId, HTMLVideoElement>
  const peerIdRef = useRef(null);
  const durationTimerRef = useRef(null);
  // ---- Call-history bookkeeping (additive; does not affect call flow) ----
  const callDirectionRef = useRef(null); // 'outgoing' | 'incoming'
  const callConnectedRef = useRef(false);
  const callConnectedAtRef = useRef(null);
  const callEndReasonRef = useRef(null); // 'rejected' when peer/we explicitly rejected
  const pendingIceCandidates = useRef([]);
  // For mesh connections (additionalPeerConnectionsRef), we need per-peer ICE candidate queues.
  // ICE candidates can arrive before remoteDescription is set; if we drop them, the peer link often never connects.
  const pendingMultiIceCandidatesRef = useRef(new Map()); // Map<userId:string, RTCIceCandidateInit[]>
  const pendingOffer = useRef(null);
  // Most-complete `existingParticipants` list we've learned about an incoming
  // multi-party invite — keyed by inviter id. The invite carries this twice:
  // once via `multiVideoCallOffer` (server-relayed, captured AT offer time so
  // it may miss invitees that the inviter sent offers to slightly later) and
  // again via `incomingMultiVideoCall` (sent AFTER the server adds the full
  // existingParticipants from the inviter). We union both so the new joiner
  // can mesh-connect with every actual participant when they accept, instead
  // of only the inviter + whoever happened to be in the call at offer time.
  const multiCallInviteParticipantsRef = useRef(new Map()); // Map<fromId:string, Set<participantId>>
  const autoAcceptCall = useRef(false);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const selectedUserRef = useRef(selectedUser);
  const cleanupDoneRef = useRef(false);
  // The user we are currently calling. Normally identical to `selectedUser`,
  // but when this component is mounted globally (App.js) without a
  // `selectedUser` prop, the chat-header button dispatches a
  // `requestStartVideoCall` event with the target user; we then store that
  // user here so endCall / call-history bookkeeping still works.
  const callTargetRef = useRef(null);
  // Tracks calls (by peer + callId) that we've just disconnected from. We use
  // this to drop *stale* incoming-call signals that may arrive in the socket
  // buffer (or via the OS push notification's click handler) right after we
  // hung up, otherwise they re-trigger the "Incoming Call" UI for a call the
  // user already ended. Entries are ignored after RECENT_END_GUARD_MS.
  const recentlyEndedCallsRef = useRef(new Map());
  const RECENT_END_GUARD_MS = 4000;
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const facingModeRef = useRef('user'); // 'user' (front) or 'environment' (back)
  // Ref-based call status so socket handlers (stale closures) can always read the latest value.
  const callStatusRef = useRef('idle');
  const sessionIdRef = useRef(`direct-video-${Math.random().toString(36).slice(2, 10)}`);
  const endCallRef = useRef(() => {});
  // Track whether the current call is multi-participant (survives stale closures).
  const isMultiParticipantCallRef = useRef(false);
  // "Call waiting": when someone tries to call us while we're already in another call,
  // we surface a banner with Accept/Reject instead of silently auto-answering or auto-rejecting.
  // Shape: { from: string, fromName: string, offer?: RTCSessionDescriptionInit }
  const [waitingCall, setWaitingCall] = useState(null);
  const waitingCallRef = useRef(null);
  const setWaitingCallSafe = (v) => { waitingCallRef.current = v; setWaitingCall(v); };

  // Held call: when we accept a second call while in another, the original call is put on
  // hold (PC kept alive, outgoing tracks paused via replaceTrack(null), peer notified).
  // Shape: { pc, peerId, name, remoteStream, callId, savedTracks: Map<RTCRtpSender, MediaStreamTrack> }
  const heldCallRef = useRef(null);
  const [hasHeldCall, setHasHeldCall] = useState(false);
  const [heldCallName, setHeldCallName] = useState('');
  // Set true when the remote peer puts US on hold (we display an "On hold" overlay).
  const [isHeldByPeer, setIsHeldByPeer] = useState(false);

  const getPreferredMediaConstraints = () => ({
    video: {
      width: { ideal: 1280 },
      height: { ideal: 720 },
      facingMode: 'user'
    },
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true
    }
  });

  const getAudioOnlyConstraints = () => ({
    video: false,
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true
    }
  });

  const hasLiveTracks = (stream) => (
    stream instanceof MediaStream && stream.getTracks().some((track) => track.readyState === 'live')
  );

  const releaseKnownMediaTracks = () => {
    const stopped = new Set();

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => {
        if (track.readyState === 'live') {
          track.stop();
          stopped.add(track.id);
        }
      });
      localStreamRef.current = null;
      setLocalStream(null);
    }

    document.querySelectorAll('video, audio').forEach((el) => {
      const src = el.srcObject;
      if (!(src instanceof MediaStream)) return;
      src.getTracks().forEach((track) => {
        if (track.readyState === 'live' && !stopped.has(track.id)) {
          track.stop();
          stopped.add(track.id);
        }
      });
    });
  };

  const acquireLocalMedia = async ({ allowReuse = true } = {}) => {
    if (allowReuse && hasLiveTracks(localStreamRef.current)) {
      return { stream: localStreamRef.current, audioOnly: false, reused: true };
    }

    const preferred = getPreferredMediaConstraints();
    const audioOnly = getAudioOnlyConstraints();
    const maxRetries = 3;

    for (let attempt = 1; attempt <= maxRetries; attempt += 1) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia(preferred);
        return { stream, audioOnly: false, reused: false };
      } catch (err) {
        const isDeviceBusy = err?.name === 'NotReadableError' || (err?.message || '').includes('Device in use');
        if (!isDeviceBusy || attempt === maxRetries) {
          break;
        }
        console.warn(`⚠️ Camera/mic busy, retrying media acquire (${attempt}/${maxRetries})...`);
        releaseKnownMediaTracks();
        await new Promise((resolve) => setTimeout(resolve, 300 + attempt * 200));
      }
    }

    const stream = await navigator.mediaDevices.getUserMedia(audioOnly);
    return { stream, audioOnly: true, reused: false };
  };

  const queueMultiIceCandidate = (userId, candidate) => {
    if (!userId || !candidate) return;
    const uid = userId.toString();
    const existing = pendingMultiIceCandidatesRef.current.get(uid) || [];
    existing.push(candidate);
    pendingMultiIceCandidatesRef.current.set(uid, existing);
  };

  const flushMultiIceCandidates = async (userId, pc) => {
    const uid = userId?.toString();
    if (!uid || !pc) return;
    const queued = pendingMultiIceCandidatesRef.current.get(uid);
    if (!queued || queued.length === 0) return;
    if (!pc.remoteDescription) return;
    pendingMultiIceCandidatesRef.current.delete(uid);
    for (const c of queued) {
      try {
        await pc.addIceCandidate(new RTCIceCandidate(c));
      } catch (err) {
        console.error('❌ Error adding queued ICE candidate for', uid, ':', err);
      }
    }
  };

  // Ringtone hook for incoming calls
  const { playRingtone, stopRingtone, resetCallAccepted } = useRingtone();

  // Keep selectedUser ref updated
  useEffect(() => {
    selectedUserRef.current = selectedUser;
  }, [selectedUser]);

  // Resolve user name by ID
  const fetchUserName = async (id) => {
    try {
      const res = await axios.get(`${config.apiUrl}/api/auth/profile/${id}?requestingUserId=${user.id}`);
      return res.data?.username || id;
    } catch (e) {
      return id;
    }
  };

  const markRecentlyEnded = (peerId, callId) => {
    const now = Date.now();
    if (peerId) recentlyEndedCallsRef.current.set(peerId.toString(), now);
    if (callId) recentlyEndedCallsRef.current.set(`call:${callId}`, now);
    // Garbage-collect old entries.
    for (const [key, ts] of recentlyEndedCallsRef.current.entries()) {
      if (now - ts > RECENT_END_GUARD_MS * 4) {
        recentlyEndedCallsRef.current.delete(key);
      }
    }
  };

  const isRecentlyEnded = ({ from, callId } = {}) => {
    const now = Date.now();
    const map = recentlyEndedCallsRef.current;
    // If a callId is present we can be precise: only match when the callId is
    // the same one we just hung up on. A *different* callId from the same peer
    // is a brand-new call and must be allowed through.
    if (callId) {
      const ts = map.get(`call:${callId}`);
      return !!(ts && now - ts <= RECENT_END_GUARD_MS);
    }
    // No callId on the event (legacy / video offer paths). Fall back to a
    // tighter peer-based window so genuine re-calls aren't suppressed for long.
    if (from) {
      const ts = map.get(from.toString());
      if (ts && now - ts <= 2000) return true;
    }
    return false;
  };

  // Ask the service worker to close any lingering OS-level call notifications
  // for the given peer so a click on a stale notification can't re-open the
  // incoming-call UI after we've already hung up.
  const clearCallNotificationsInSW = (peerId) => {
    try {
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CLEAR_CALL_NOTIFICATIONS',
          data: { from: peerId ? peerId.toString() : undefined, callType: 'video' },
        });
      }
    } catch (_) { /* best effort */ }
  };

  const getParticipantDisplayName = (id) => {
    const uid = id?.toString?.() || String(id || '');
    if (!uid) return 'Participant';
    if (uid === (user?.id?.toString?.() || String(user?.id || ''))) {
      const myName = user?.username || user?.name || 'You';
      return `${myName} (You)`;
    }
    return participantNames[uid] || uid;
  };

  // Keep a name map for all visible remote participants so every tile shows username.
  useEffect(() => {
    let cancelled = false;
    const myId = user.id?.toString();
    const ids = Array.from(allParticipants).filter(id => id && id !== myId);
    // Also include IDs from streams in case allParticipants is behind
    remoteParticipantStreams.forEach((p) => {
      const uid = p.userId?.toString?.() || String(p.userId || '');
      if (uid && !ids.includes(uid)) ids.push(uid);
    });

    ids.forEach(async (uid) => {
      if (participantNames[uid]) return;
      const name = await fetchUserName(uid);
      if (!cancelled) {
        setParticipantNames((prev) => (prev[uid] ? prev : { ...prev, [uid]: name }));
      }
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allParticipants, remoteParticipantStreams]);

  // --- Multi-participant rendering helpers ---
  const syncRemoteParticipants = () => {
    const entries = Array.from(remoteStreamsRef.current.entries())
      .filter(([uid, stream]) => uid && stream && typeof stream.getTracks === 'function' && stream.getTracks().length > 0)
      .map(([uid, stream]) => ({ userId: uid.toString(), stream }));
    entries.sort((a, b) => a.userId.localeCompare(b.userId));
    setRemoteParticipantStreams(entries);
  };

  const upsertRemoteStream = (userId, stream) => {
    if (!userId || !stream) return;
    const uid = userId.toString();
    remoteStreamsRef.current.set(uid, stream);
    syncRemoteParticipants();
  };

  // Merge a freshly-received `existingParticipants` list (from either
  // `multiVideoCallOffer` or `incomingMultiVideoCall`) into the running
  // snapshot for `fromId`, and keep the pendingOffer in sync. The new joiner
  // needs the union of both events so mesh links can be established with
  // every actual participant on accept (the offer event captures the list at
  // the time the offer was relayed, the invite event captures it after the
  // server has folded in all existingParticipants from the inviter).
  const recordInviteParticipants = (fromId, participants) => {
    if (!fromId) return;
    const key = fromId.toString();
    const set = multiCallInviteParticipantsRef.current.get(key) || new Set();
    if (Array.isArray(participants)) {
      participants.forEach((p) => {
        const s = p?.toString();
        if (s) set.add(s);
      });
    }
    multiCallInviteParticipantsRef.current.set(key, set);
    if (pendingOffer.current && pendingOffer.current.from?.toString() === key) {
      pendingOffer.current.existingParticipants = Array.from(set);
    }
  };

  // Listen for service worker messages (video call accept/reject from notification)
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleServiceWorkerMessage = (event) => {
      const { type, data } = event.data || {};
      
      // Handle ringtone control from service worker
      if (type === 'PLAY_RINGTONE') {
        console.log('🔔 Service worker requested ringtone play');
        resetCallAccepted();
        playRingtone();
        return;
      }
      
      if (type === 'STOP_RINGTONE') {
        console.log('🔕 Service worker requested ringtone stop');
        stopRingtone();
        return;
      }
      
      if (type === 'VIDEO_CALL_ACCEPT' && data?.from) {
        console.log('📞 Accepting video call from notification:', data.from);

        // If this notification corresponds to a call we *just* hung up, the
        // OS-level notification was still on screen and the click is a stale
        // accept — ignore it so it doesn't re-open the incoming call UI.
        if (isRecentlyEnded({ from: data.from, callId: data.callId })) {
          console.log('🚫 Ignoring stale VIDEO_CALL_ACCEPT for recently ended call');
          stopRingtone();
          clearCallNotificationsInSW(data.from);
          return;
        }

        // Stop ringtone when accepting from notification
        stopRingtone();
        setCallerId(data.from);
        setCallStatus('incoming');
        setError(null);
        setCallerName(data.callerName || data.from);
        setRemoteName(data.callerName || data.from);
        peerIdRef.current = data.from;
        autoAcceptCall.current = true;
      } else if (type === 'VIDEO_CALL_REJECT' && data?.from) {
        console.log('📞 Rejecting video call from notification:', data.from);
        // Stop ringtone when rejecting from notification
        stopRingtone();
        if (socket) {
          socket.emit('videoCallRejected', {
            from: user.id,
            to: data.from
          });
        }
        setIsIncomingCall(false);
        setCallStatus('idle');
        setCallerId(null);
        setCallerName(null);
      }
    };

    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);

    return () => {
      navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, user.id]);

  // Check URL for video call parameter (when app opens from notification)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const videoCallFrom = urlParams.get('videoCallFrom');
    
    if (videoCallFrom) {
      console.log('📞 App opened with video call from:', videoCallFrom);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Stop ringtone when call status changes from incoming to connecting/connected
  useEffect(() => {
    if (callStatus === 'connecting' || callStatus === 'connected') {
      // Ensure ringtone is stopped when call is accepted/connected
      stopRingtone();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callStatus]);

  // Stop ringtone when incoming call flag changes to false
  useEffect(() => {
    if (!isIncomingCall && callStatus !== 'incoming') {
      // Ensure ringtone is stopped when incoming call ends
      stopRingtone();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIncomingCall, callStatus]);

  useEffect(() => {
    if (!socket) return;

    const onIncoming = async (data) => {
      console.log('📞 Incoming video call:', data);

      // Drop signaling that belongs to a call we just disconnected from. This
      // covers the race where, right after we hang up, a queued incoming-call
      // event lands in the socket buffer and would otherwise re-show the
      // Accept/Reject modal for a call that's already over.
      if (isRecentlyEnded({ from: data?.from, callId: data?.callId })) {
        console.log('🚫 Ignoring stale incomingVideoCall from', data?.from);
        return;
      }

      // If we’re already in an active call, surface this as a "call waiting" notification
      // (Accept / Reject) rather than silently auto-answering (privacy bug) or auto-rejecting
      // (no notification). Use callStatusRef for live status.
      const liveStatus = callStatusRef.current;
      const fromId = data?.from?.toString();
      const currentPeerId = peerIdRef.current?.toString();
      const inActiveCall = liveStatus === 'connected' || liveStatus === 'calling' || liveStatus === 'connecting';
      if (inActiveCall && fromId && fromId !== currentPeerId) {
        console.warn('🔔 Busy: showing call-waiting notification for', fromId, '(currently with', currentPeerId, ')');
        const waitingName = await fetchUserName(fromId);
        setWaitingCallSafe({
          from: fromId,
          fromName: waitingName || fromId,
          offer: waitingCallRef.current?.from === fromId ? waitingCallRef.current?.offer : undefined,
        });
        return;
      }
      if (inActiveCall) {
        console.warn('⚠️ Ignoring incomingVideoCall while already in a call');
        return;
      }
      setCallerId(data.from);
      setCallStatus('incoming');
      setError(null);
      const name = await fetchUserName(data.from);
      setCallerName(name);
      setRemoteName(name);
      peerIdRef.current = data.from;
      
      // Reset call accepted flag for new incoming  call
      resetCallAccepted();
      
      // Play ringtone for incoming call (only for receiver, not caller)
      playRingtone();
      
      if (autoAcceptCall.current && data.from === peerIdRef.current) {
        console.log('✅ Auto-accepting call from notification');
        autoAcceptCall.current = false;
        setIsIncomingCall(false);
      } else {
        setIsIncomingCall(true);
      }
    };

    const onAccepted = () => {
      console.log('✅ CALLER: Video call accepted');
      // Stop ringtone when call is accepted (for caller side)
      stopRingtone();
      
      // If we're already connected (e.g. multi-party or duplicate event), ignore
      if (callStatus === 'connected') {
        console.warn('⚠️ CALLER: Already connected, ignoring duplicate videoCallAccepted');
        return;
      }
      
      const pc = peerConnectionRef.current;
      const localStream = localStreamRef.current;
      const remoteStream = remoteStreamRef.current;
      
      if (!pc) {
        console.error('❌ CALLER: No peer connection when call accepted!');
        console.error('❌ CALLER: callStatus =', callStatus, ', may be a stale event');
        // Don't set error if call is already ended/idle - this is a stale event
        if (callStatus === 'calling') {
          setError('Connection lost. Please try again.');
        }
        return;
      }
      
      if (!localStream) {
        console.error('❌ CALLER: No local stream when call accepted!');
        setError('Local media lost. Please try again.');
        return;
      }
      
      setLocalStream(localStream);
      if (remoteStream) {
        setRemoteStream(remoteStream);
      }
      
      const currentSelectedUser = selectedUserRef.current;
      setRemoteName((prev) => prev || currentSelectedUser?.username || currentSelectedUser?.id || 'User');
      setCallStatus('connected');
      setIsCallActive(true);
      startCallTimer();
      
      // Track initial participant
      const participantId = currentSelectedUser?.id?.toString();
      if (participantId) {
        setAllParticipants(prev => {
          const newSet = new Set(prev);
          newSet.add(participantId);
          newSet.add(user.id?.toString());
          return newSet;
        });
      }
    };

    const onRejected = () => {
      // Stop ringtone when call is rejected
      stopRingtone();
      callEndReasonRef.current = 'rejected';
      setCallStatus('ended');
      setError('Call was rejected');
      handleEndCall();
    };

    const onEnded = (data = {}) => {
      // If a HELD peer ended the call, just clean up the held slot — don't tear down the
      // active call that's currently in progress.
      const fromId = data?.from?.toString();
      const held = heldCallRef.current;
      if (fromId && held && fromId === held.peerId?.toString()) {
        try { if (held.pc && held.pc.signalingState !== 'closed') held.pc.close(); } catch (_) {}
        heldCallRef.current = null;
        setHasHeldCall(false);
        setHeldCallName('');
        return;
      }

      // In a multi-participant conference call, one peer leaving should not end the
      // whole call — just remove that peer. Only end if no remote peers remain.
      if (isMultiParticipantCallRef.current && fromId) {
        // Close the additional PC for this peer (if any)
        const additionalPc = additionalPeerConnectionsRef.current.get(fromId);
        if (additionalPc) {
          try { additionalPc.close(); } catch (_) {}
          additionalPeerConnectionsRef.current.delete(fromId);
        }
        // If the leaving peer is the main (active) peer, close its PC
        if (fromId === peerIdRef.current?.toString()) {
          const mainPc = peerConnectionRef.current;
          if (mainPc) {
            try { if (mainPc.signalingState !== 'closed') mainPc.close(); } catch (_) {}
          }
          peerConnectionRef.current = null;
          peerIdRef.current = null;
        }
        // Remove their remote stream tile
        remoteStreamsRef.current.delete(fromId);
        syncRemoteParticipants();
        // Remove from allParticipants set
        setAllParticipants(prev => { const next = new Set(prev); next.delete(fromId); return next; });

        // Check if any remote peers remain — if not, end the call
        const remainingRemote = remoteStreamsRef.current.size + additionalPeerConnectionsRef.current.size;
        if (remainingRemote === 0) {
          stopRingtone();
          setCallStatus('ended');
          handleEndCall();
        }
        return;
      }

      // Stop ringtone when call ends (caller cancelled)
      stopRingtone();
      setCallStatus('ended');
      handleEndCall();
    };

    const onIce = async (data) => {
      // Route to the right PC by sender id, so candidates from a held peer aren't applied
      // to the active call (and vice-versa).
      const fromId = data?.from?.toString();
      let peerConnection = peerConnectionRef.current;
      const held = heldCallRef.current;
      if (fromId && held && fromId === held.peerId?.toString()) {
        peerConnection = held.pc;
      }
      if (!peerConnection) {
        console.warn('⚠️ No peer connection yet, queueing candidate');
        pendingIceCandidates.current.push(data.candidate);
        return;
      }
      
      if (peerConnection.signalingState === 'closed') {
        console.warn('⚠️ Connection closed, ignoring ICE candidate');
        return;
      }
      
      if (peerConnection.remoteDescription) {
        try {
          await peerConnection.addIceCandidate(new RTCIceCandidate(data.candidate));
        } catch (err) {
          console.error('❌ addIceCandidate error:', err.message);
        }
      } else {
        pendingIceCandidates.current.push(data.candidate);
      }
    };

    const onOffer = async (data) => {
      console.log('📩 Received video offer from:', data.from);

      // Drop offers that belong to a call we just hung up on.
      if (isRecentlyEnded({ from: data?.from, callId: data?.callId })) {
        console.log('🚫 Ignoring stale video offer from', data?.from);
        return;
      }

      // Refresh rejoin from the person we're already talking to. Answer in place.
      // Never auto-answer a resume offer once this side has left the call.
      if (data?.resume) {
        const resumeFrom = data?.from?.toString();
        const resumePeer = peerIdRef.current?.toString();
        const resumeStatus = callStatusRef.current;
        const stillHere = resumeStatus === 'connected' || resumeStatus === 'calling' || resumeStatus === 'connecting';
        if (stillHere && resumeFrom && resumeFrom === resumePeer) {
          if (data.callId) callIdRef.current = data.callId;
          console.log('🔄 Accepting video rejoin offer from', resumeFrom);
          setError('Reconnecting…');
          await handleOffer(data.offer, data.from);
          setError(null);
        } else {
          console.log('🚫 Ignoring video rejoin offer; call is no longer active');
        }
        return;
      }

      // SECURITY/PRIVACY: If we are already in a 1:1 call with someone else, never auto-answer
      // an offer from a different peer. Store it on the call-waiting record so the user can
      // explicitly Accept (which ends the current call) or Reject from the banner.
      const liveStatus = callStatusRef.current;
      const fromId = data?.from?.toString();
      const currentPeerId = peerIdRef.current?.toString();
      const inActiveCall = liveStatus === 'connected' || liveStatus === 'calling' || liveStatus === 'connecting';
      const isFromOtherPeer = !!fromId && !!currentPeerId && fromId !== currentPeerId;
      if (inActiveCall && isFromOtherPeer) {
        console.warn('🔔 Busy: stashing offer from', fromId, 'as waiting call (currently with', currentPeerId, ')');
        const existing = waitingCallRef.current;
        const waitingName = existing?.from === fromId
          ? existing.fromName
          : await fetchUserName(fromId);
        setWaitingCallSafe({
          from: fromId,
          fromName: waitingName || fromId,
          offer: data.offer,
        });
        return;
      }

      pendingOffer.current = { offer: data.offer, from: data.from };
      
      // Play ringtone when offer is received (if we haven't already and call is not accepted)
      // This ensures ringtone plays even if incomingVideoCall event was missed
      // But don't play if call is already connecting/connected/ended
      if (!isIncomingCall && liveStatus !== 'incoming' && liveStatus !== 'connecting' && liveStatus !== 'connected' && liveStatus !== 'ended') {
        console.log('🔔 Playing ringtone on offer receipt');
        setCallStatus('incoming');
        setIsIncomingCall(true);
        setCallerId(data.from);
        const name = await fetchUserName(data.from);
        setCallerName(name);
        setRemoteName(name);
        peerIdRef.current = data.from;
        playRingtone();
      }
      
      if (autoAcceptCall.current) {
        console.log('✅ Auto-accept: Processing offer immediately');
        autoAcceptCall.current = false;
        
        socket.emit('videoCallAccepted', {
          from: user.id,
          to: data.from
        });
        
        setIsIncomingCall(false);
        await handleOffer(data.offer, data.from);
        return;
      }
      
      if (liveStatus === 'incoming') {
        console.log('📦 Incoming call: Offer stored, waiting for user to accept');
        return;
      }
      
      // Only process an unsolicited offer mid-call if it is from the SAME peer we're already
      // talking to (e.g. ICE renegotiation). Offers from any other peer have already been
      // rejected above.
      if ((liveStatus === 'connected' || liveStatus === 'calling') && fromId && fromId === currentPeerId) {
        console.log('✅ Call active: Processing renegotiation offer from current peer');
        await handleOffer(data.offer, data.from);
        return;
      }

      if (liveStatus === 'connected' || liveStatus === 'calling') {
        // Different peer or unknown peer while in call — already handled by reject above,
        // but keep an explicit guard so we never silently answer.
        console.warn('⚠️ Ignoring videoOffer mid-call from non-current peer');
        return;
      }
      
      console.log('⚠️ Unexpected state, processing offer anyway');
      await handleOffer(data.offer, data.from);
    };

    const onAnswer = async (data) => {
      await handleAnswer(data.answer);
    };

    socket.on('incomingVideoCall', onIncoming);
    socket.on('videoCallAccepted', onAccepted);
    socket.on('videoCallRejected', onRejected);
    socket.on('videoCallEnded', onEnded);
    socket.on('videoIceCandidate', onIce);
    socket.on('videoOffer', onOffer);
    socket.on('videoAnswer', onAnswer);

    // Handle multi-participant call answers
    const onMultiVideoAnswer = async (data) => {
      const { from, answer } = data;
      const fromId = from?.toString();
      console.log('📥 Multi-video call answer received from:', fromId);
      
      const pc = additionalPeerConnectionsRef.current.get(fromId);
      if (!pc) {
        console.warn('⚠️ No peer connection found for invited participant:', from);
        return;
      }
      if (pc.signalingState === 'closed') {
        console.warn('⚠️ Peer connection closed for', from, ', ignoring answer');
        return;
      }
      // Only set remote answer if we're in have-local-offer state (we sent an offer and are waiting)
      if (pc.signalingState !== 'have-local-offer') {
        console.warn(`⚠️ Ignoring answer from ${from}: PC in '${pc.signalingState}' state (expected 'have-local-offer')`);
        return;
      }
      try {
        await pc.setRemoteDescription(new RTCSessionDescription(answer));
        console.log('✅ Set remote description for invited participant:', from);
        await flushMultiIceCandidates(fromId, pc);
      } catch (err) {
        console.error('❌ Error setting remote description for', from, ':', err);
      }
    };

    // Handle multi-participant ICE candidates
    const onMultiVideoIceCandidate = async (data) => {
      const { from, candidate } = data;
      const fromId = from?.toString();
      if (!candidate || !fromId) return;

      // Check additional PCs first, then fall back to the primary peerConnectionRef
      let pc = additionalPeerConnectionsRef.current.get(fromId);
      if (!pc && peerIdRef.current?.toString() === fromId && peerConnectionRef.current) {
        pc = peerConnectionRef.current;
      }

      if (pc && pc.remoteDescription) {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (err) {
          console.error('❌ Error adding ICE candidate for', from, ':', err);
        }
      } else {
        // Queue until we have a PC + remoteDescription
        queueMultiIceCandidate(fromId, candidate);
      }
    };

    socket.on('multiVideoCallAnswer', onMultiVideoAnswer);
    socket.on('multiVideoIceCandidate', onMultiVideoIceCandidate);

    // Handle incoming multi-participant video call invitation
    const onIncomingMultiVideoCall = async (data) => {
      console.log('📞 Incoming multi-video call invitation:', data);
      const { from, callId, existingParticipants, callerName } = data;

      // Drop invites that belong to a call we just hung up on.
      if (isRecentlyEnded({ from, callId })) {
        console.log('🚫 Ignoring stale incomingMultiVideoCall from', from);
        return;
      }

      // The invite is the most authoritative source of the current participants
      // list (the server folds in the inviter's `existingParticipants` AFTER any
      // sibling-invitee `multiVideoCallOffer`s have already been processed).
      // Remember it so `acceptCall` -> `handleMultiVideoCallOffer` can mesh with
      // every actual participant, not just the inviter.
      recordInviteParticipants(from, existingParticipants);

      // Store call info
      if (callId) {
        callIdRef.current = callId;
      }
      // Mark this as multi-participant so all handlers know.
      isMultiParticipantCallRef.current = true;

      // If we're already in an active call, this invite is either misrouted or redundant.
      // Don’t interrupt the ongoing call UI; the proper flow for existing participants is
      // `multiVideoCallParticipantJoined` + `multiVideoCallOffer`.
      if (callStatus === 'connected' || callStatus === 'calling' || callStatus === 'connecting') {
        console.warn('⚠️ Ignoring incomingMultiVideoCall while already in a call', { from, callId });
        return;
      }
      
      setCallerId(from);
      setCallStatus('incoming');
      setError(null);
      const name = await fetchUserName(from);
      setCallerName(name || callerName || 'Someone');
      setRemoteName(name || callerName || 'Someone');
      peerIdRef.current = from;
      
      // Reset call accepted flag for new incoming call
      resetCallAccepted();
      
      // Play ringtone for incoming call
      playRingtone();
      
      setIsIncomingCall(true);
    };

    // Handle multi-participant video call offer
    const onMultiVideoCallOffer = async (data) => {
      console.log('📩 Received multi-video call offer from:', data.from);
      const { from, offer, callId, existingParticipants } = data;
      const myId = user.id?.toString();
      const fromId = from?.toString();

      // Drop offers that belong to a call we just hung up on.
      if (isRecentlyEnded({ from, callId })) {
        console.log('🚫 Ignoring stale multi-video offer from', fromId);
        return;
      }
      
      // Store call info
      if (callId) {
        callIdRef.current = callId;
      }

      // If we're already in a call, this is an offer from another participant (mesh connection)
      // Use refs (not closure-captured callStatus) to avoid stale-closure race conditions.
      const liveStatus = callStatusRef.current;
      const isActivelyInCall = (liveStatus === 'connected' || liveStatus === 'calling' || isMultiParticipantCallRef.current) && localStreamRef.current;
      if (isActivelyInCall) {
        console.log('🔗 Received offer from participant in ongoing call:', fromId, '(liveStatus:', liveStatus, ')');
        console.log(`🔍 ID comparison: My ID: ${myId}, Their ID: ${fromId}`);
        
        try {
          // The refreshed peer's old primary connection is dead. Detach it before
          // answering so its failure doesn't tear the conference down.
          if (data.resume && fromId && fromId === peerIdRef.current?.toString() && peerConnectionRef.current) {
            const oldPrimary = peerConnectionRef.current;
            peerConnectionRef.current = null;
            try { oldPrimary.close(); } catch (_) {}
          }

          // Get or create peer connection
          let pc = additionalPeerConnectionsRef.current.get(fromId);
          
          // Handle offer collision (glare): if we already sent an offer to this peer,
          // the "polite peer" (higher ID) rolls back and accepts the incoming offer.
          if (pc && pc.signalingState === 'have-local-offer') {
            const imPolite = myId > fromId; // higher ID is the polite peer
            if (imPolite) {
              console.log(`🔄 Offer collision with ${fromId}: I'm polite (higher ID), rolling back my offer`);
              await pc.setLocalDescription({ type: 'rollback' });
              // PC is now back in 'stable' — fall through to set the incoming offer
            } else {
              console.log(`🔄 Offer collision with ${fromId}: I'm impolite (lower ID), ignoring their offer`);
              return; // our offer takes priority, remote will answer it
            }
          }
          
          const replacePc = !!data.resume || !pc || pc.signalingState === 'closed' || pc.connectionState === 'failed' || pc.connectionState === 'disconnected' || pc.connectionState === 'closed';
          if (replacePc) {
            // Create peer connection WITHOUT sending offer (we're the responder)
            if (pc) {
              try { pc.close(); } catch (_) {}
              additionalPeerConnectionsRef.current.delete(fromId);
            }
            console.log(`📦 Creating peer connection to respond to ${fromId}`);
            pc = await createPeerConnectionWithParticipant(fromId, false);
          }
          
          if (!pc) {
            throw new Error('Failed to create peer connection');
          }
          
          // Correct responder order: setRemoteDescription → addTrack → createAnswer
          console.log(`📥 Setting remote description from ${fromId}`);
          await pc.setRemoteDescription(new RTCSessionDescription(offer));

          // Add local tracks AFTER setRemoteDescription so transceivers match the offer
          const localStream = localStreamRef.current;
          if (localStream) {
            const existingSenders = pc.getSenders().filter(s => s.track);
            if (existingSenders.length === 0) {
              localStream.getTracks().forEach(track => {
                pc.addTrack(track, localStream);
              });
            }
          }

          await flushMultiIceCandidates(fromId, pc);

          // Create and send answer
          console.log(`📤 Creating answer for ${fromId}`);
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          
          socket.emit('multiVideoCallAnswer', {
            from: myId,
            to: fromId,
            answer: answer,
            callId: callId
          });
          
          console.log(`✅ Sent answer to participant ${fromId}`);
          
          // Mark as conference and ensure self + original peer are tracked
          isMultiParticipantCallRef.current = true;
          const primaryPeerId = peerIdRef.current?.toString();
          setAllParticipants(prev => {
            const newSet = new Set(prev);
            newSet.add(fromId);
            if (myId) newSet.add(myId);
            if (primaryPeerId) newSet.add(primaryPeerId);
            return newSet;
          });
          // Ensure the original peer's stream appears in participant grid
          if (primaryPeerId && remoteStreamRef.current && !remoteStreamsRef.current.has(primaryPeerId)) {
            upsertRemoteStream(primaryPeerId, remoteStreamRef.current);
          }
        } catch (err) {
          console.error(`❌ Error handling offer from participant ${fromId}:`, err);
        }
        return;
      }
      
      // Record this event's participants too — if the offer was relayed by
      // the server before any sibling invites, this list may be smaller than
      // the invite's, but we still union it in so anything fresh is captured.
      recordInviteParticipants(from, existingParticipants);
      const knownParticipants = Array.from(
        multiCallInviteParticipantsRef.current.get(from?.toString()) || []
      );

      // Defensive: if we already have a pendingOffer from a DIFFERENT user
      // (i.e. the primary inviter), don't overwrite it with a stray mesh
      // offer from a sibling joiner. With the server-side `accepted`-set
      // fix this shouldn't happen, but we keep the guard so an older or
      // misbehaving server can't break the receiver UI by overwriting the
      // inviter's offer with one we can't yet act on. The sender will retry
      // automatically via `multiVideoCallParticipantJoined` once we accept.
      if (pendingOffer.current && pendingOffer.current.from && pendingOffer.current.from.toString() !== fromId) {
        console.log('🚫 Ignoring stray mesh offer from', fromId, '— already have pending offer from', pendingOffer.current.from);
        return;
      }

      // Store the offer for when user accepts (initial invitation)
      pendingOffer.current = {
        offer: offer,
        from: from,
        isMultiParticipant: true,
        existingParticipants: knownParticipants,
      };

      // If we're already in incoming state, the offer is ready
      if (callStatus === 'incoming' && from === callerId) {
        console.log('📦 Multi-video call offer stored, waiting for user to accept');
        return;
      }

      // If we're not in incoming state yet, trigger it
      if (callStatus !== 'incoming' && callStatus !== 'connected' && callStatus !== 'calling') {
        console.log('🔔 Playing ringtone on multi-video call offer receipt');
        setCallStatus('incoming');
        setIsIncomingCall(true);
        setCallerId(from);
        const name = await fetchUserName(from);
        setCallerName(name);
        setRemoteName(name);
        peerIdRef.current = from;
        playRingtone();
      }

      // Auto-accept if flag is set
      if (autoAcceptCall.current && from === peerIdRef.current) {
        console.log('✅ Auto-accept: Processing multi-video call offer immediately');
        autoAcceptCall.current = false;
        setIsIncomingCall(false);
        await handleMultiVideoCallOffer(offer, from, callId, knownParticipants);
        return;
      }
    };

    // Handle when a new participant joins the call
    const onParticipantJoined = async (data) => {
      const { newParticipantId, callId, existingParticipants } = data;
      const myId = user.id?.toString();
      console.log(`👋 New participant ${newParticipantId} joined the call`, { callId, existingParticipants });
      console.log(`🔍 Tiebreaker: My ID: ${myId}, New participant ID: ${newParticipantId}`);

      // Adopt the shared callId so all participants use the same one for signaling.
      if (callId) {
        callIdRef.current = callId;
      }
      // We're now in a multi-participant call.
      isMultiParticipantCallRef.current = true;

      // Heal mesh: ensure we know the full participant list and connect to anyone we're missing.
      const normalizedExisting = Array.isArray(existingParticipants)
        ? existingParticipants.map(p => p?.toString()).filter(Boolean)
        : [];
      const normalizedNew = newParticipantId?.toString();
      const participantsToEnsure = new Set([
        ...normalizedExisting,
        ...(normalizedNew ? [normalizedNew] : []),
      ]);

      // Update participants list in state
      setAllParticipants(prev => {
        const newSet = new Set(prev);
        participantsToEnsure.forEach(pid => newSet.add(pid));
        return newSet;
      });

      // Create peer connections to ALL participants (except ourselves and primary 1:1 peer).
      // This prevents the "receivers only see caller" issue if a join event was missed earlier.
      const primaryPeerId = peerIdRef.current?.toString();
      if (localStreamRef.current) {
        for (const pid of participantsToEnsure) {
          if (!pid || pid === myId) continue;
          // Skip the primary 1:1 peer - already connected via peerConnectionRef
          const forceReconnect = !!data.resume && pid === normalizedNew;
          if (forceReconnect && pid === primaryPeerId && peerConnectionRef.current) {
            const oldPrimary = peerConnectionRef.current;
            peerConnectionRef.current = null;
            try { oldPrimary.close(); } catch (_) {}
          }
          if (!forceReconnect && pid === primaryPeerId && peerConnectionRef.current
              && peerConnectionRef.current.signalingState !== 'closed'
              && peerConnectionRef.current.connectionState !== 'failed'
              && peerConnectionRef.current.connectionState !== 'disconnected') {
            console.log(`⏭️ Skipping ${pid}: already connected via primary 1:1 connection`);
            // Register their stream for the multi-participant grid if not present
            if (remoteStreamRef.current && !remoteStreamsRef.current.has(pid)) {
              upsertRemoteStream(pid, remoteStreamRef.current);
            }
            continue;
          }
          try {
            if (forceReconnect) {
              const stale = additionalPeerConnectionsRef.current.get(pid);
              if (stale) {
                try { stale.close(); } catch (_) {}
                additionalPeerConnectionsRef.current.delete(pid);
              }
            }
            const existingPc = additionalPeerConnectionsRef.current.get(pid);
            const needsConnection = forceReconnect || !existingPc
              || existingPc.signalingState === 'closed'
              || existingPc.connectionState === 'failed'
              || existingPc.connectionState === 'disconnected'
              || existingPc.connectionState === 'closed';
            if (needsConnection) {
              const pc = await createPeerConnectionWithParticipant(pid);
              if (pc) {
                console.log(`✅ Ensured peer connection with participant ${pid}`);
              }
            } else {
              console.log(`⏭️ Skipping ${pid}: connection exists (state=${existingPc.connectionState})`);
            }
          } catch (err) {
            console.error(`❌ Failed to ensure connection to participant ${pid}:`, err);
          }
        }
      }
    };

    socket.on('incomingMultiVideoCall', onIncomingMultiVideoCall);
    socket.on('multiVideoCallOffer', onMultiVideoCallOffer);
    socket.on('multiVideoCallParticipantJoined', onParticipantJoined);

    // A specific peer left a multi-participant call (clicked End or socket disconnected).
    // Remove only their PC + tile; the rest of the conference stays active.
    const onMultiVideoCallParticipantLeft = (data = {}) => {
      const leftId = data?.participantId?.toString();
      if (!leftId) return;
      console.log(`👋 Multi-video participant left: ${leftId}`);
      const pc = additionalPeerConnectionsRef.current.get(leftId);
      if (pc) {
        try { if (pc.signalingState !== 'closed') pc.close(); } catch (_) {}
        additionalPeerConnectionsRef.current.delete(leftId);
      }
      // If the leaver is the primary 1:1 peer, also close peerConnectionRef.
      if (leftId === peerIdRef.current?.toString()) {
        const mainPc = peerConnectionRef.current;
        if (mainPc) {
          try { if (mainPc.signalingState !== 'closed') mainPc.close(); } catch (_) {}
        }
        peerConnectionRef.current = null;
      }
      remoteStreamsRef.current.delete(leftId);
      const videoEl = remoteTileVideoElsRef.current.get(leftId);
      if (videoEl) {
        try { videoEl.srcObject = null; } catch (_) {}
        remoteTileVideoElsRef.current.delete(leftId);
      }
      pendingMultiIceCandidatesRef.current.delete(leftId);
      setAllParticipants(prev => { const next = new Set(prev); next.delete(leftId); return next; });
      syncRemoteParticipants();
      // If no remote peers remain at all, end the call locally.
      const remainingRemote = remoteStreamsRef.current.size + additionalPeerConnectionsRef.current.size;
      if (remainingRemote === 0 && isMultiParticipantCallRef.current) {
        stopRingtone();
        setCallStatus('ended');
        handleEndCall();
      }
    };
    socket.on('multiVideoCallParticipantLeft', onMultiVideoCallParticipantLeft);

    const onCallLimitReached = (data = {}) => {
      setError(data.message || `Conference call is limited to ${MAX_CALL_PARTICIPANTS} members.`);
    };
    socket.on('callLimitReached', onCallLimitReached);

    // Hold / resume from the remote peer. We only show the indicator if the message comes
    // from whichever peer is currently active (so a held peer's events don't flicker our UI).
    const onRemoteHold = (data = {}) => {
      const fromId = data?.from?.toString();
      if (!fromId) return;
      const activePeer = peerConnectionRef.current?._peerId || peerIdRef.current;
      if (fromId === activePeer?.toString()) {
        setIsHeldByPeer(true);
      }
    };
    const onRemoteResume = (data = {}) => {
      const fromId = data?.from?.toString();
      if (!fromId) return;
      const activePeer = peerConnectionRef.current?._peerId || peerIdRef.current;
      if (fromId === activePeer?.toString()) {
        setIsHeldByPeer(false);
      }
    };
    socket.on('videoCallHold', onRemoteHold);
    socket.on('videoCallResume', onRemoteResume);

    return () => {
      socket.off('incomingVideoCall', onIncoming);
      socket.off('videoCallAccepted', onAccepted);
      socket.off('videoCallRejected', onRejected);
      socket.off('videoCallEnded', onEnded);
      socket.off('videoIceCandidate', onIce);
      socket.off('videoOffer', onOffer);
      socket.off('videoAnswer', onAnswer);
      socket.off('multiVideoCallAnswer', onMultiVideoAnswer);
      socket.off('multiVideoIceCandidate', onMultiVideoIceCandidate);
      socket.off('incomingMultiVideoCall', onIncomingMultiVideoCall);
      socket.off('multiVideoCallOffer', onMultiVideoCallOffer);
      socket.off('multiVideoCallParticipantJoined', onParticipantJoined);
      socket.off('multiVideoCallParticipantLeft', onMultiVideoCallParticipantLeft);
      socket.off('callLimitReached', onCallLimitReached);
      socket.off('videoCallHold', onRemoteHold);
      socket.off('videoCallResume', onRemoteResume);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, callStatus, user.id]);

  // Track call direction and connection state for call-history logging.
  // Observes existing callStatus transitions only; never alters them.
  useEffect(() => {
    if (callStatus === 'calling' && !callDirectionRef.current) {
      callDirectionRef.current = 'outgoing';
    } else if (callStatus === 'incoming' && !callDirectionRef.current) {
      callDirectionRef.current = 'incoming';
    }
    if (callStatus === 'connected' && !callConnectedRef.current) {
      callConnectedRef.current = true;
      callConnectedAtRef.current = Date.now();
    }
  }, [callStatus]);

  const startCallTimer = () => {
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
    const origin = callConnectedAtRef.current || Date.now();
    if (!callConnectedAtRef.current) callConnectedAtRef.current = origin;
    const tick = () => setCallDuration(Math.max(0, Math.round((Date.now() - origin) / 1000)));
    tick();
    durationTimerRef.current = setInterval(tick, 1000);
  };

  const stopCallTimer = () => {
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
  };

  const scheduleCallRecovery = (pc, { restartIce = false, quiet = false } = {}) => {
    if (!quiet) setError('Reconnecting…');
    if (restartIce) {
      try {
        reconnectAttemptsRef.current += 1;
        if (
          reconnectAttemptsRef.current <= 2 &&
          typeof pc.restartIce === 'function' &&
          pc.signalingState !== 'closed'
        ) {
          pc.restartIce();
        }
      } catch (_) {}
    }
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    reconnectTimerRef.current = setTimeout(() => {
      if (peerConnectionRef.current !== pc) return;
      const down = pc.connectionState === 'failed'
        || pc.connectionState === 'disconnected'
        || pc.connectionState === 'closed'
        || pc.iceConnectionState === 'failed'
        || pc.iceConnectionState === 'disconnected';
      if (!down) return;
      setError('Call connection failed');
      endCallRef.current?.();
    }, CALL_RECONNECT_GRACE_MS);
  };

  const initializePeerConnection = () => {
    // Clean up any existing connection — but NEVER close a PC that we've stashed as a held call.
    // Detach the ref first so the old PC's "closed" event can't end the new call.
    if (peerConnectionRef.current && peerConnectionRef.current !== heldCallRef.current?.pc) {
      const previous = peerConnectionRef.current;
      peerConnectionRef.current = null;
      try {
        previous.close();
      } catch (err) {
        console.warn('Previous peer connection close error:', err);
      }
    }

    const pc = new RTCPeerConnection({
      iceServers: config.iceServers || [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
        { urls: 'stun:stun2.l.google.com:19302' }
      ],
      iceCandidatePoolSize: 10
    });

    pc.onicecandidate = (event) => {
      // Use this PC's own peerId (set right after creation) so a held PC continues to route
      // its trickle-ICE candidates to the correct (held) peer even after we've swapped to
      // another active call.
      const targetPeerId = pc._peerId || peerIdRef.current;
      if (event.candidate && targetPeerId) {
        socket.emit('videoIceCandidate', {
          candidate: event.candidate,
          from: user.id,
          to: targetPeerId
        });
      }
    };

    pc.ontrack = (event) => {
      console.log('🎥 VideoCall: ontrack event fired!', event);
      console.log('🎥 Track details:', {
        kind: event.track?.kind,
        id: event.track?.id,
        enabled: event.track?.enabled,
        readyState: event.track?.readyState,
        streamsCount: event.streams?.length
      });

      // If this PC is NOT the currently-active one (e.g. it's the held call), do NOT
      // overwrite global stream state. That would clobber the active call's video/audio.
      if (peerConnectionRef.current && peerConnectionRef.current !== pc) {
        console.log('⏸️ VideoCall: ontrack from non-active PC (held?), skipping global update');
        return;
      }
      
      // Get or create remote stream
      let stream = remoteStreamRef.current;
      
      if (event.streams && event.streams[0]) {
        // Use the stream from the event if available
        stream = event.streams[0];
        console.log('🎥 VideoCall: Using stream from event with', stream.getTracks().length, 'tracks');
      } else if (event.track) {
        // If we don't have a stream yet, create one
        if (!stream) {
          stream = new MediaStream();
          console.log('🎥 VideoCall: Created new MediaStream');
        }
        
        // Add the track to the stream if it's not already there
        const existingTrack = stream.getTracks().find(t => t.id === event.track.id);
        if (!existingTrack) {
          stream.addTrack(event.track);
          console.log('🎥 VideoCall: Added', event.track.kind, 'track to stream');
        } else {
          console.log('🎥 VideoCall: Track already in stream, skipping');
        }
      } else {
        console.error('❌ VideoCall: No streams or tracks in ontrack event');
        return;
      }
      
      console.log('📡 Setting remoteStream state with tracks:', stream.getTracks().map(t => ({
        kind: t.kind,
        id: t.id.substr(0, 8),
        enabled: t.enabled,
        readyState: t.readyState
      })));
      
      // Update both state and ref
      setRemoteStream(stream);
      remoteStreamRef.current = stream;

      // Also store as a participant stream so multi-user UI can render everyone.
      if (peerIdRef.current) {
        upsertRemoteStream(peerIdRef.current, stream);
      }
      
      // Force video element update
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = stream;
        remoteVideoRef.current.muted = false;
        remoteVideoRef.current.play().catch(err => {
          console.warn('Remote video play error in ontrack:', err);
        });
      }
      
      console.log('✅ Remote stream state set and video element updated');
    };

    pc.onconnectionstatechange = () => {
      console.log('🔌 Connection state:', pc.connectionState);
      // Only the PC currently stored as the active call can end it. A replaced
      // or held connection going down must be ignored.
      if (peerConnectionRef.current !== pc) return;
      if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        scheduleCallRecovery(pc, {
          restartIce: pc.connectionState === 'failed',
          quiet: pc.connectionState === 'disconnected',
        });
      } else if (pc.connectionState === 'closed') {
        setError('Call connection closed');
        endCallRef.current?.();
      } else if (pc.connectionState === 'connected') {
        console.log('✅ Peer connection established successfully');
        setError(null);
        reconnectAttemptsRef.current = 0;
        if (reconnectTimerRef.current) {
          clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = null;
        }
      }
    };

    pc.oniceconnectionstatechange = () => {
      console.log('🧊 ICE connection state:', pc.iceConnectionState);
      if (peerConnectionRef.current !== pc) return;
      if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') {
        scheduleCallRecovery(pc, {
          restartIce: pc.iceConnectionState === 'failed',
          quiet: pc.iceConnectionState === 'disconnected',
        });
      } else if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
        console.log('✅ ICE connection established');
        setError(null);
        reconnectAttemptsRef.current = 0;
        if (reconnectTimerRef.current) {
          clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = null;
        }
      }
    };

    pc.onicegatheringstatechange = () => {
      // ICE gathering state change
    };

    peerConnectionRef.current = pc;
    return pc;
  };

  const startCall = async (overrideUser = null, options = null) => {
    const callTarget = overrideUser || selectedUser;
    if (!callTarget) {
      setError('Please select a user to call');
      return;
    }
    callTargetRef.current = callTarget;

    try {
      const left = await leaveOtherCallSessions(sessionIdRef.current);
      if (left > 0) await new Promise((resolve) => setTimeout(resolve, 200));
      setError(options?.resume ? 'Reconnecting…' : null);
      cleanupDoneRef.current = false;
      if (options?.callId) callIdRef.current = options.callId;
      if (!callIdRef.current) {
        callIdRef.current = `call-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      }
      
      const media = await acquireLocalMedia({ allowReuse: true });
      const stream = media.stream;
      if (media.audioOnly) {
        console.log('✅ CALLER: Got audio-only stream (no camera)');
        setError('Camera not available, audio-only mode');
      } else {
        console.log(media.reused ? '✅ CALLER: Reusing existing local media stream' : '✅ CALLER: Got video + audio stream');
      }
      
      setLocalStream(stream);
      localStreamRef.current = stream;
      if (options?.isMuted) {
        stream.getAudioTracks().forEach((track) => { track.enabled = false; });
        setIsMuted(true);
      }
      if (options?.isVideoOff) {
        stream.getVideoTracks().forEach((track) => { track.enabled = false; });
        setIsVideoOff(true);
      }
      
      const pc = initializePeerConnection();
      console.log('✅ CALLER: Peer connection created');
      
      // Add local tracks to peer connection
      console.log('🎥 CALLER: Adding local tracks to peer connection');
      stream.getTracks().forEach(track => {
        console.log('🎥 CALLER: Adding track:', track.kind, track.enabled);
        pc.addTrack(track, stream);
      });

      const offer = await pc.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true
      });
      await pc.setLocalDescription(offer);

      setRemoteName(callTarget?.username || callTarget?.id || 'User');
      peerIdRef.current = callTarget.id;
      pc._peerId = callTarget.id;

      console.log('📞 CALLER: Sending video call offer to', callTarget.id);
      socket.emit('videoCallOffer', {
        offer: offer,
        from: user.id,
        to: callTarget.id,
        callId: callIdRef.current,
        callerName: user?.username || user?.name || user?.email || user.id,
        resume: !!options?.resume,
      });

      if (options?.resume && options?.wasConnected) {
        setIsCallActive(true);
        setCallStatus('connecting');
        startCallTimer();
      } else {
        setCallStatus('calling');
      }
    } catch (error) {
      console.error('Error starting call:', error);
      if (error.name === 'NotAllowedError') {
        setError('Camera/microphone access denied. Please allow permissions.');
      } else if (error.name === 'NotFoundError') {
        setError('No camera/microphone found. Please connect devices.');
      } else {
        setError('Failed to start call. Please try again.');
      }
    }
  };

  const handleOffer = async (offer, fromId) => {
    console.log('📞🔵 RECEIVER: handleOffer START');
    
    try {
      setError(null);
      cleanupDoneRef.current = false;
      console.log('🎥 STEP 1: Requesting camera/mic...');
      let media;
      try {
        media = await acquireLocalMedia({ allowReuse: true });
      } catch (mediaErr) {
        console.error('❌ STEP 1 FAILED:', mediaErr.name, mediaErr.message);
        throw new Error('Cannot access media: ' + mediaErr.message);
      }

      const stream = media.stream;
      if (media.audioOnly) {
        setError('Camera not available');
      }
      
      setLocalStream(stream);
      localStreamRef.current = stream;
      
      console.log('✅ STEP 2: Initializing peer connection...');
      const pc = initializePeerConnection();
      
      peerIdRef.current = fromId || callerId || peerIdRef.current;
      pc._peerId = peerIdRef.current;
      console.log('Peer ID:', peerIdRef.current);
      
      // CRITICAL FIX: Set remote description FIRST (creates transceivers from offer)
      console.log('✅ STEP 3: Setting remote description (offer)...');
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      console.log('✅ Remote description set');
      
      // THEN add local tracks (will upgrade transceivers to sendrecv)
      console.log('✅ STEP 4: Adding local tracks to peer connection...');
      stream.getTracks().forEach((track) => {
        console.log(`  Track: ${track.kind} ${track.id.substr(0,8)}`);
        pc.addTrack(track, stream);
      });

      // Process pending ICE candidates
      console.log('✅ STEP 5: Processing ICE candidates...');
      const pendingCount = pendingIceCandidates.current.length;
      console.log(`Found ${pendingCount} pending candidates`);
      while (pendingIceCandidates.current.length > 0) {
        const candidate = pendingIceCandidates.current.shift();
        try {
          await pc.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (err) {
          console.warn('ICE candidate error:', err);
        }
      }

      console.log('✅ STEP 6: Creating answer...');
      const answer = await pc.createAnswer();
      
      console.log('✅ STEP 7: Setting local description...');
      await pc.setLocalDescription(answer);

      console.log('✅ STEP 8: Sending answer...');
      socket.emit('videoCallAnswer', {
        answer: answer,
        from: user.id,
        to: fromId || callerId
      });

      console.log('✅ STEP 9: Setting call status...');
      setRemoteName((prev) => prev || callerName || remoteName || selectedUser?.username || 'Unknown User');
      setCallStatus('connected');
      setIsCallActive(true);
      startCallTimer();
      
      console.log('🎉🔵 RECEIVER: handleOffer COMPLETE!');
    } catch (error) {
      console.error('❌🔴 RECEIVER: handleOffer FAILED!');
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      setError('Call failed: ' + error.message);
      
      setTimeout(() => handleEndCall(), 3000);
    }
  };

  const handleAnswer = async (answer) => {
    try {
      const peerConnection = peerConnectionRef.current;
      if (!peerConnection) {
        console.error('❌ CALLER: No peer connection when handling answer!');
        // Don't set error if not in an active calling state
        if (callStatus === 'calling' || callStatus === 'connected') {
          setError('Peer connection lost. Please try again.');
        }
        return;
      }
      
      // Guard: only set remote answer if we're in have-local-offer state
      if (peerConnection.signalingState !== 'have-local-offer') {
        console.warn(`⚠️ CALLER: Ignoring answer, signaling state is '${peerConnection.signalingState}' (expected 'have-local-offer')`);
        return;
      }
      
      console.log('✅ CALLER: Handling answer');
      
      await peerConnection.setRemoteDescription(new RTCSessionDescription(answer));
      console.log('✅ CALLER: Remote description set successfully');
      
      // Process pending ICE candidates
      while (pendingIceCandidates.current.length > 0) {
        const candidate = pendingIceCandidates.current.shift();
        try {
          await peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (err) {
          console.error('Error adding pending ICE candidate:', err);
        }
      }
      
      setRemoteName((prev) => prev || selectedUser?.username || selectedUser?.id || 'User');
      
      console.log('✅ CALLER: Answer processed');
    } catch (error) {
      console.error('❌ CALLER: Error handling answer:', error);
      setError('Failed to establish call connection.');
    }
  };

  // Create peer connection with another participant (for mesh topology)
  const createPeerConnectionWithParticipant = async (participantId, shouldSendOffer = null) => {
    const myId = user.id?.toString();
    const otherUserId = participantId?.toString();
    
    // Check for existing connection - reuse if healthy, remove if stale
    if (additionalPeerConnectionsRef.current.has(otherUserId)) {
      const existingPc = additionalPeerConnectionsRef.current.get(otherUserId);
      const state = existingPc?.connectionState;
      const sigState = existingPc?.signalingState;
      if (sigState !== 'closed' && state !== 'failed' && state !== 'closed') {
        console.log(`⚠️ Already have connection with ${otherUserId} (state=${state}, signalingState=${sigState})`);
        return existingPc;
      }
      // Stale/failed connection — tear down and recreate
      console.log(`🔄 Replacing stale connection with ${otherUserId} (state=${state}, signalingState=${sigState})`);
      try { existingPc.close(); } catch (_) {}
      additionalPeerConnectionsRef.current.delete(otherUserId);
    }

    if (!localStreamRef.current) {
      console.error('❌ No local stream available for peer connection');
      return null;
    }

    try {
      const localStream = localStreamRef.current;
      const callId = callIdRef.current;

      // Tiebreaker: Use ID comparison to determine who should initiate
      // Only send offer if our ID is smaller (to avoid both sides sending offers)
      const isInitiator = shouldSendOffer !== null 
        ? shouldSendOffer 
        : (myId < otherUserId);
      
      console.log(`🔗 Creating peer connection with ${otherUserId} (${isInitiator ? 'INITIATOR - will send offer' : 'RESPONDER - will wait for offer'})`);

      // Create peer connection
      const pc = new RTCPeerConnection({
        iceServers: config.iceServers || [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
          { urls: 'stun:stun2.l.google.com:19302' }
        ],
        iceCandidatePoolSize: 10
      });

      // Store peer connection
      additionalPeerConnectionsRef.current.set(otherUserId, pc);

      // Handle ICE candidates
      pc.onicecandidate = (event) => {
        if (event.candidate && socket) {
          socket.emit('multiVideoIceCandidate', {
            from: user.id,
            to: participantId,
            candidate: event.candidate,
            callId: callId
          });
        }
      };

      // Handle remote stream from this participant
      pc.ontrack = (event) => {
        console.log(`📹 Received track from participant ${participantId}:`, event.track.kind);
        
        let stream = remoteStreamsRef.current.get(participantId);
        
        if (event.streams && event.streams[0]) {
          stream = event.streams[0];
          console.log(`📹 Using stream from event for ${participantId}`);
        } else if (event.track) {
          if (!stream) {
            stream = new MediaStream();
            console.log(`📹 Created new stream for ${participantId}`);
          }
          
          const existingTrack = stream.getTracks().find(t => t.id === event.track.id);
          if (!existingTrack) {
            stream.addTrack(event.track);
            console.log(`📹 Added ${event.track.kind} track to stream for ${participantId}`);
          }
        }

        if (stream) {
          upsertRemoteStream(participantId, stream);
          console.log(`✅ Stream stored for participant ${participantId} with ${stream.getTracks().length} tracks`);
          
          // Update main remote stream if this is the first/primary participant
          if (participantId === (selectedUser?.id?.toString() || callerId?.toString() || peerIdRef.current?.toString())) {
            setRemoteStream(stream);
            remoteStreamRef.current = stream;
            if (remoteVideoRef.current) {
              remoteVideoRef.current.srcObject = stream;
              remoteVideoRef.current.play().catch(err => {
                console.warn('Remote video play error:', err);
              });
            }
          }
        }
      };

      if (isInitiator) {
        // Initiator: add tracks first, then create and send offer
        localStream.getTracks().forEach(track => {
          pc.addTrack(track, localStream);
          console.log(`➕ Added ${track.kind} track to PC for ${otherUserId}`);
        });

        console.log(`📤 Creating and sending offer to ${otherUserId}`);
        const offer = await pc.createOffer({
          offerToReceiveAudio: true,
          offerToReceiveVideo: true
        });
        await pc.setLocalDescription(offer);

        socket.emit('multiVideoCallOffer', {
          from: myId,
          to: otherUserId,
          offer: offer,
          callId: callId,
          resume: sendingResumeOffersRef.current,
        });

        console.log(`✅ Sent offer to participant ${otherUserId}`);
      } else {
        // Responder: do NOT add tracks here. The caller will add tracks
        // after setRemoteDescription (correct WebRTC order for responders).
        console.log(`⏳ Waiting for offer from ${otherUserId}`);
      }

      return pc;
    } catch (error) {
      console.error(`❌ Error creating peer connection with ${otherUserId}:`, error);
      additionalPeerConnectionsRef.current.delete(otherUserId);
      throw error;
    }
  };

  // Handle multi-video call offer acceptance (for third person joining)
  const handleMultiVideoCallOffer = async (offer, fromId, callId, existingParticipantsFromInvite = []) => {
    console.log('📞🔵 RECEIVER: handleMultiVideoCallOffer START', { fromId, callId, existingParticipantsFromInvite });
    
    try {
      setError(null);
      cleanupDoneRef.current = false;
      console.log('🎥 STEP 1: Requesting camera/mic for multi-video call...');
      let media;
      try {
        media = await acquireLocalMedia({ allowReuse: true });
      } catch (mediaErr) {
        console.error('❌ STEP 1 FAILED:', mediaErr.name, mediaErr.message);
        throw new Error('Cannot access media: ' + mediaErr.message);
      }

      const stream = media.stream;
      if (media.audioOnly) {
        setError('Camera not available');
      }
      
      setLocalStream(stream);
      localStreamRef.current = stream;
      
      console.log('✅ STEP 2: Creating peer connection for multi-video call...');
      
      // Create a new peer connection for this multi-participant call
      const pc = new RTCPeerConnection({
        iceServers: config.iceServers || [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
          { urls: 'stun:stun2.l.google.com:19302' }
        ],
        iceCandidatePoolSize: 10
      });

      // Store this as the main peer connection for multi-participant call
      peerConnectionRef.current = pc;
      peerIdRef.current = fromId;
      
      // Handle ICE candidates
      pc.onicecandidate = (event) => {
        if (event.candidate && socket) {
          socket.emit('multiVideoIceCandidate', {
            from: user.id,
            to: fromId,
            candidate: event.candidate,
            callId: callId
          });
        }
      };

      // Handle remote stream
      pc.ontrack = (event) => {
        console.log('🎥 Multi-video call: ontrack event fired!', event);
        console.log('🎥 Track details:', {
          kind: event.track?.kind,
          id: event.track?.id,
          enabled: event.track?.enabled,
          readyState: event.track?.readyState,
          streamsCount: event.streams?.length
        });
        
        // If this PC is NOT the currently-active one, skip global state updates.
        if (peerConnectionRef.current && peerConnectionRef.current !== pc) {
          console.log('⏸️ Multi-video: ontrack from non-active PC, skipping global update');
          // Still store per-participant stream for grid rendering.
          if (fromId) upsertRemoteStream(fromId, event.streams?.[0] || new MediaStream([event.track]));
          return;
        }

        // Get or create remote stream
        let stream = remoteStreamRef.current;
        
        if (event.streams && event.streams[0]) {
          // Use the stream from the event if available
          stream = event.streams[0];
          console.log('🎥 Multi-video call: Using stream from event with', stream.getTracks().length, 'tracks');
        } else if (event.track) {
          // If we don't have a stream yet, create one
          if (!stream) {
            stream = new MediaStream();
            console.log('🎥 Multi-video call: Created new MediaStream');
          }
          
          // Add the track to the stream if it's not already there
          const existingTrack = stream.getTracks().find(t => t.id === event.track.id);
          if (!existingTrack) {
            stream.addTrack(event.track);
            console.log('🎥 Multi-video call: Added', event.track.kind, 'track to stream');
          } else {
            console.log('🎥 Multi-video call: Track already in stream, skipping');
          }
        } else {
          console.error('❌ Multi-video call: No streams or tracks in ontrack event');
          return;
        }
        
        console.log('📡 Multi-video call: Setting remoteStream state with tracks:', stream.getTracks().map(t => ({
          kind: t.kind,
          id: t.id.substr(0, 8),
          enabled: t.enabled,
          readyState: t.readyState
        })));
        
        // Update both state and ref
        setRemoteStream(stream);
        remoteStreamRef.current = stream;
        upsertRemoteStream(fromId, stream);
        
        // Force video element update
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = stream;
          remoteVideoRef.current.muted = false;
          remoteVideoRef.current.play().catch(err => {
            console.warn('Multi-video call: Remote video play error in ontrack:', err);
          });
        }
        
        console.log('✅ Multi-video call: Remote stream state set and video element updated');
      };

      pc.onconnectionstatechange = () => {
        console.log('🔌 Multi-video call connection state:', pc.connectionState);
        if (peerConnectionRef.current !== pc) return;
        if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
          scheduleCallRecovery(pc, {
            restartIce: pc.connectionState === 'failed',
            quiet: pc.connectionState === 'disconnected',
          });
        } else if (pc.connectionState === 'closed') {
          setError('Call connection failed');
          endCallRef.current?.();
        } else if (pc.connectionState === 'connected') {
          console.log('✅ Multi-video call peer connection established successfully');
          setError(null);
          if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
          }
        }
      };

      pc.oniceconnectionstatechange = () => {
        console.log('🧊 Multi-video call ICE connection state:', pc.iceConnectionState);
        if (peerConnectionRef.current !== pc) return;
        if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') {
          scheduleCallRecovery(pc, {
            restartIce: pc.iceConnectionState === 'failed',
            quiet: pc.iceConnectionState === 'disconnected',
          });
        } else if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
          console.log('✅ Multi-video call ICE connection established');
          setError(null);
          if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
          }
        }
      };
      
      // CRITICAL FIX: Set remote description FIRST (creates transceivers from offer)
      console.log('✅ STEP 3: Setting remote description (multi-video call offer)...');
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      console.log('✅ Remote description set for multi-video call');
      
      // THEN add local tracks (will upgrade transceivers to sendrecv)
      console.log('✅ STEP 4: Adding local tracks to peer connection...');
      stream.getTracks().forEach((track) => {
        console.log(`  Track: ${track.kind} ${track.id.substr(0,8)}`);
        pc.addTrack(track, stream);
      });

      console.log('✅ STEP 5: Creating answer for multi-video call...');
      const answer = await pc.createAnswer();
      
      console.log('✅ STEP 6: Setting local description...');
      await pc.setLocalDescription(answer);

      console.log('✅ STEP 7: Sending answer for multi-video call...');
      socket.emit('multiVideoCallAnswer', {
        answer: answer,
        from: user.id,
        to: fromId,
        callId: callId
      });

      // Use existing participants from invite, or fallback to current list
      const allExistingParticipants = existingParticipantsFromInvite.length > 0 
        ? existingParticipantsFromInvite.map(p => p?.toString()).filter(Boolean)
        : Array.from(allParticipants);
      
      // Ensure we include ourselves and the inviter
      const currentParticipants = [...allExistingParticipants];
      const myId = user.id?.toString();
      const inviterId = fromId?.toString();
      
      if (!currentParticipants.includes(myId)) {
        currentParticipants.push(myId);
      }
      if (!currentParticipants.includes(inviterId)) {
        currentParticipants.push(inviterId);
      }

      // Update participants list with all existing participants
      setAllParticipants(new Set(currentParticipants));

      // Notify that call was accepted (with complete participants list)
      socket.emit('multiVideoCallAccepted', {
        from: user.id,
        to: fromId,
        callId: callId,
        existingParticipants: currentParticipants
      });

      // IMPORTANT: Set connected BEFORE mesh so incoming offers are handled as mesh connections.
      console.log('✅ STEP 8: Setting call status to connected (before mesh setup)...');
      setRemoteName((prev) => prev || callerName || remoteName || 'Unknown User');
      setCallStatus('connected');
      setIsCallActive(true);
      isMultiParticipantCallRef.current = true;
      startCallTimer();

      // Create connections to ALL existing participants (except ourselves and inviter who we're already connected to)
      const otherParticipants = currentParticipants.filter(
        p => p && p !== myId && p !== inviterId
      );

      console.log(`🔗 New participant creating connections to ${otherParticipants.length} other participants:`, otherParticipants);
      console.log(`📋 All participants in call:`, currentParticipants);
      console.log(`👤 My ID: ${myId}, Inviter ID: ${inviterId}`);

      // Create peer connections to all other participants (mesh topology)
      // The tiebreaker logic will determine who sends the offer
      for (const participantId of otherParticipants) {
        try {
          if (!additionalPeerConnectionsRef.current.has(participantId)) {
            const pc = await createPeerConnectionWithParticipant(participantId);
            if (pc) {
              console.log(`✅ Established peer connection with participant ${participantId}`);
            }
          } else {
            console.log(`⚠️ Already have connection to ${participantId}, skipping`);
          }
        } catch (err) {
          console.error(`❌ Failed to create connection to ${participantId}:`, err);
        }
      }

      console.log('🎉🔵 RECEIVER: handleMultiVideoCallOffer COMPLETE!');
    } catch (error) {
      console.error('❌🔴 RECEIVER: handleMultiVideoCallOffer FAILED!');
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      setError('Call failed: ' + error.message);
      
      // Notify rejection
      if (socket && fromId) {
        socket.emit('multiVideoCallRejected', {
          from: user.id,
          to: fromId,
          callId: callId
        });
      }
      
      setTimeout(() => handleEndCall(), 3000);
    }
  };

  const acceptCall = async () => {
    if (!callerId) return;
    console.log('✅ VideoCall: User clicked ACCEPT button, caller:', callerId);

    const left = await leaveOtherCallSessions(sessionIdRef.current);
    if (left > 0) await new Promise((resolve) => setTimeout(resolve, 200));
    
    // End any active audio call before accepting this video call.
    window.dispatchEvent(new CustomEvent('endOtherCallTypes', { detail: { acceptedType: 'video' } }));

    // Stop ringtone IMMEDIATELY when user accepts the call
    console.log('🛑 Stopping ringtone - call accepted');
    stopRingtone();
    setIsIncomingCall(false); // Set this early to prevent ringtone from restarting
    
    setRemoteName((prev) => prev || callerName || remoteName || 'Unknown User');
    
    // Process the pending offer now that user has accepted
    if (pendingOffer.current) {
      console.log('✅ Processing stored offer after user accepted');
      
      // Check if this is a multi-participant call
      if (pendingOffer.current.isMultiParticipant) {
        console.log('📞 This is a multi-participant call');
        const callId = callIdRef.current;
        
        // NOTE: Do NOT emit multiVideoCallAccepted here.
        // handleMultiVideoCallOffer() emits it with the full participant list.
        
        // Handle multi-participant call offer
        await handleMultiVideoCallOffer(
          pendingOffer.current.offer, 
          pendingOffer.current.from,
          callId,
          pendingOffer.current.existingParticipants || []
        );
      } else {
        // Regular one-on-one call
        socket.emit('videoCallAccepted', {
          from: user.id,
          to: callerId
        });
        
        await handleOffer(pendingOffer.current.offer, pendingOffer.current.from);
      }
      
      pendingOffer.current = null;
    } else {
      console.log('⚠️ No pending offer found, waiting for offer...');
      // Only emit 1:1 videoCallAccepted if this is NOT a multi-participant call.
      // For multi-party, handleMultiVideoCallOffer will emit multiVideoCallAccepted
      // once the offer arrives. Sending videoCallAccepted to the caller's 1:1 handler
      // causes "No peer connection" errors because the caller is already connected.
      if (isMultiParticipantCallRef.current || callIdRef.current) {
        console.log('📞 Multi-party call: waiting for offer before emitting accepted');
        autoAcceptCall.current = true;
      } else {
        socket.emit('videoCallAccepted', {
          from: user.id,
          to: callerId
        });
      }
    }
  };  const rejectCall = () => {
    if (!callerId) return;
    
    // Stop ringtone when user rejects the call
    stopRingtone();
    
    // Check if this is a multi-participant call
    if (pendingOffer.current?.isMultiParticipant) {
      const callId = callIdRef.current;
      socket.emit('multiVideoCallRejected', {
        from: user.id,
        to: callerId,
        callId: callId
      });
    } else {
      socket.emit('videoCallRejected', {
        from: user.id,
        to: callerId
      });
    }
    
    setIsIncomingCall(false);
    setCallStatus('ended');
    setError('Call rejected');
    pendingOffer.current = null;
  };

  // ---- Call waiting (incoming call while already in another call) ----
  const rejectWaitingCall = () => {
    const w = waitingCallRef.current;
    if (!w) return;
    try {
      socket.emit('videoCallRejected', { from: user.id, to: w.from });
    } catch (_) { /* best effort */ }
    setWaitingCallSafe(null);
  };

  // ---- Call hold (telephone-style) ----
  // Pause sending media to the active peer (replaceTrack(null) per sender) and notify them
  // they're on hold. Returns a snapshot the caller can stash to later resume the call.
  const holdActivePeer = async () => {
    const pc = peerConnectionRef.current;
    if (!pc) return null;
    const savedTracks = new Map();
    for (const sender of pc.getSenders()) {
      if (sender?.track) {
        savedTracks.set(sender, sender.track);
        try { await sender.replaceTrack(null); } catch (err) { console.warn('hold: replaceTrack(null) failed', err); }
      }
    }
    // Also mute/freeze INCOMING media from the held peer so the holder no longer
    // sees or hears them until they swap back. Save prior enabled state to restore.
    const mutedReceiverTracks = [];
    try {
      for (const receiver of pc.getReceivers()) {
        const t = receiver?.track;
        if (t && t.enabled) {
          mutedReceiverTracks.push(t);
          t.enabled = false;
        }
      }
    } catch (err) { console.warn('hold: mute receivers failed', err); }
    const snapshot = {
      pc,
      peerId: pc._peerId || peerIdRef.current,
      name: remoteName || callerName || 'Unknown',
      remoteStream: remoteStreamRef.current,
      callId: callIdRef.current,
      savedTracks,
      mutedReceiverTracks,
    };
    if (snapshot.peerId) {
      try {
        socket.emit('videoCallHold', { from: user.id, to: snapshot.peerId, callId: snapshot.callId });
      } catch (_) { /* best effort */ }
    }
    return snapshot;
  };

  // Restore sending media to a previously held peer and notify them.
  const restoreHeldPeer = async (snapshot) => {
    if (!snapshot) return;
    for (const [sender, track] of snapshot.savedTracks) {
      try { await sender.replaceTrack(track); } catch (err) { console.warn('resume: replaceTrack failed', err); }
    }
    // Re-enable previously-muted incoming tracks so holder sees/hears them again.
    if (Array.isArray(snapshot.mutedReceiverTracks)) {
      for (const t of snapshot.mutedReceiverTracks) {
        try { t.enabled = true; } catch (_) { /* ignore */ }
      }
    }
    if (snapshot.peerId) {
      try {
        socket.emit('videoCallResume', { from: user.id, to: snapshot.peerId, callId: snapshot.callId });
      } catch (_) { /* best effort */ }
    }
  };

  // Swap: resume the held call and put the currently-active call on hold.
  const swapHeldCall = async () => {
    const held = heldCallRef.current;
    if (!held) return;
    // Hold the currently-active call.
    const newlyHeld = await holdActivePeer();
    // Restore held — re-enables sender + receiver tracks and notifies peer.
    await restoreHeldPeer(held);
    // Swap pointers so the held call becomes active.
    peerConnectionRef.current = held.pc;
    peerIdRef.current = held.peerId;
    callIdRef.current = held.callId;
    setRemoteName(held.name);
    setCallerName(held.name);
    setCallerId(held.peerId);

    // Build a fresh stream from the restored PC's live receiver tracks.
    // The snapshot's remoteStream may be stale (tracks ended while on hold),
    // so always prefer live tracks from the peer connection.
    let activeStream = null;
    try {
      const liveTracks = held.pc.getReceivers()
        .map(r => r.track)
        .filter(t => t && t.readyState === 'live');
      if (liveTracks.length) {
        activeStream = new MediaStream(liveTracks);
      }
    } catch (_) { /* best effort */ }
    // Fall back to the snapshot stream if no live tracks yet (peer still resuming).
    if (!activeStream) {
      activeStream = held.remoteStream;
    }
    remoteStreamRef.current = activeStream;
    setRemoteStream(activeStream);

    // Update participant stream map so multi-user grid stays correct.
    if (held.peerId && activeStream) {
      upsertRemoteStream(held.peerId, activeStream);
    }

    // Imperatively set the video element — React does not bind srcObject.
    // Clear first so the browser detects the change even if it's the same object.
    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null;
      remoteVideoRef.current.srcObject = activeStream;
      if (activeStream) {
        remoteVideoRef.current.play().catch(() => {});
      }
    }

    // Newly-held replaces previously-held in the slot (if any).
    heldCallRef.current = newlyHeld || null;
    if (newlyHeld) {
      // Remove the newly-held peer from the visible participant grid so only the
      // active call's tile is rendered (held calls must not show a video tile).
      if (newlyHeld.peerId) {
        remoteStreamsRef.current.delete(newlyHeld.peerId.toString());
        syncRemoteParticipants();
      }
      setHasHeldCall(true);
      setHeldCallName(newlyHeld.name);
    } else {
      setHasHeldCall(false);
      setHeldCallName('');
    }
  };

  // End just the held call, without disturbing the active call.
  const endHeldCall = () => {
    const held = heldCallRef.current;
    if (!held) return;
    if (held.peerId) {
      try {
        socket.emit('videoCallEnded', { from: user.id, to: held.peerId, callId: held.callId });
      } catch (_) { /* best effort */ }
    }
    try {
      if (held.pc && held.pc.signalingState !== 'closed') held.pc.close();
    } catch (_) { /* best effort */ }
    heldCallRef.current = null;
    setHasHeldCall(false);
    setHeldCallName('');
  };

  // Leave a conference call cleanly so we can accept a new incoming call.
  // Notifies all conference peers, closes mesh PCs, and resets conference state
  // WITHOUT resetting isCallActive (the new call will keep us active).
  const leaveConferenceForNewCall = () => {
    if (!isMultiParticipantCallRef.current) return;
    console.log('📞 VideoCall: leaving conference to accept a new call');

    const peerId = peerIdRef.current || selectedUser?.id || callTargetRef.current?.id;
    // Notify all conference peers
    if (peerId && socket?.connected) {
      socket.emit('videoCallEnded', { from: user.id, to: peerId });
    }
    additionalPeerConnectionsRef.current.forEach((_pc, participantId) => {
      if (participantId !== peerId?.toString() && socket?.connected) {
        socket.emit('videoCallEnded', { from: user.id, to: participantId });
      }
    });

    // Close main peer connection
    const mainPc = peerConnectionRef.current;
    if (mainPc && mainPc.signalingState !== 'closed') {
      try { mainPc.close(); } catch (_) {}
    }
    peerConnectionRef.current = null;

    // Close all mesh peer connections
    additionalPeerConnectionsRef.current.forEach((pc) => {
      try { if (pc && pc.signalingState !== 'closed') pc.close(); } catch (_) {}
    });
    additionalPeerConnectionsRef.current.clear();

    // Stop local/remote streams (new call will re-acquire)
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    if (remoteStreamRef.current) {
      remoteStreamRef.current.getTracks().forEach((t) => t.stop());
      remoteStreamRef.current = null;
    }
    if (localVideoRef.current) localVideoRef.current.srcObject = null;
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;

    // Clear conference-specific state
    remoteStreamsRef.current.clear();
    remoteTileVideoElsRef.current.clear();
    setRemoteParticipantStreams([]);
    setAllParticipants(new Set());
    isMultiParticipantCallRef.current = false;
    setShowAddParticipants(false);
    setSelectedContacts(new Set());
    stopCallTimer();
  };

  const acceptWaitingCall = async () => {
    const w = waitingCallRef.current;
    if (!w) return;

    const left = await leaveOtherCallSessions(sessionIdRef.current);
    if (left > 0) await new Promise((resolve) => setTimeout(resolve, 200));

    if (isMultiParticipantCallRef.current) {
      // Leave the conference — can't hold a mesh call
      leaveConferenceForNewCall();
    } else {
      // Existing hold logic for 1:1 calls
      if (heldCallRef.current && heldCallRef.current.peerId !== w.from) {
        endHeldCall();
      }

      // 1) Put the current active call on hold (keep PC alive, pause media, notify peer).
      const snapshot = await holdActivePeer();
      if (snapshot) {
        heldCallRef.current = snapshot;
        setHasHeldCall(true);
        setHeldCallName(snapshot.name || 'Unknown');
        // Remove the held peer from the visible participant grid so only the new
        // active call's tile will be rendered.
        if (snapshot.peerId) {
          remoteStreamsRef.current.delete(snapshot.peerId.toString());
          syncRemoteParticipants();
        }
      }
    }

    // 2) Reset just the active-peer state for the new caller (without tearing down media).
    setError(null);
    cleanupDoneRef.current = false;
    setRemoteStream(null);
    remoteStreamRef.current = null;
    if (remoteVideoRef.current) {
      try { remoteVideoRef.current.srcObject = null; } catch (_) { /* ignore */ }
    }
    // The new active peer's PC will be created by handleOffer/initializePeerConnection.
    // initializePeerConnection skips closing peerConnectionRef.current when it's the held PC.
    setCallerId(w.from);
    setCallerName(w.fromName);
    setRemoteName(w.fromName);
    peerIdRef.current = w.from;
    callIdRef.current = null;
    setCallStatus('incoming');
    setIsIncomingCall(false);
    setWaitingCallSafe(null);

    if (w.offer) {
      pendingOffer.current = { offer: w.offer, from: w.from };
      try {
        socket.emit('videoCallAccepted', { from: user.id, to: w.from });
      } catch (_) { /* best effort */ }
      await handleOffer(w.offer, w.from);
      pendingOffer.current = null;
    } else {
      // Offer hasn't arrived yet — let onOffer auto-accept it.
      autoAcceptCall.current = true;
    }
  };

  const endCall = () => {
    // Close add participants modal if open
    if (showAddParticipants) {
      setShowAddParticipants(false);
      setSelectedContacts(new Set());
    }

    // In multi-participant mode, emit a single multiVideoCallEnd so the server can broadcast
    // multiVideoCallParticipantLeft to every remaining participant. Their conference stays
    // active; only OUR tile is removed for them.
    if (isMultiParticipantCallRef.current && callIdRef.current) {
      try {
        socket.emit('multiVideoCallEnd', {
          from: user.id,
          callId: callIdRef.current,
        });
      } catch (_) { /* best effort */ }
    } else {
      // 1:1 call — notify the single peer.
      const peerId = peerIdRef.current || selectedUser?.id || callTargetRef.current?.id;
      if (peerId) {
        socket.emit('videoCallEnded', {
          from: user.id,
          to: peerId
        });
      }
    }
    handleEndCall();
  };
  endCallRef.current = endCall;

  const handleEndCall = () => {
    if (isPageUnloading()) return;
    if (cleanupDoneRef.current) return;
    cleanupDoneRef.current = true;
    clearActiveCall('video');
    
    console.log('🎥 VideoCall: Ending call and cleaning up...');

    // Remember which peer/call we just disconnected from so we can drop any
    // late-arriving "incoming call" signals (socket replays + stale OS
    // notification clicks) from the same caller for a few seconds.
    const endedPeerId = (peerIdRef.current || callerId || selectedUser?.id || callTargetRef.current?.id || null);
    const endedCallId = callIdRef.current;
    markRecentlyEnded(endedPeerId, endedCallId);
    if (isMultiParticipantCallRef.current) {
      additionalPeerConnectionsRef.current.forEach((_pc, pid) => markRecentlyEnded(pid, endedCallId));
    }

    // Close any system push notifications for this caller so a later click
    // can't re-trigger VIDEO_CALL_ACCEPT.
    clearCallNotificationsInSW(endedPeerId);
    
    // Close add participants modal if open
    setShowAddParticipants(false);
    setSelectedContacts(new Set());
    
    // Stop ringtone when call ends
    stopRingtone();
    // Reset flag for next call
    resetCallAccepted();
    
    // Log call to history before cleanup
    const callTargetForLog = selectedUser || callTargetRef.current;
    if (onEndCall && (callTargetForLog || callerId)) {
      const targetUser = callTargetForLog || { id: callerId, username: callerName };
      const direction = callDirectionRef.current || (callStatus === 'incoming' ? 'incoming' : 'outgoing');

      // Derive the call-history status without affecting any call flow.
      const wasConnected = callConnectedRef.current;
      let callHistoryStatus;
      if (wasConnected) callHistoryStatus = 'answered';
      else if (callEndReasonRef.current === 'rejected') callHistoryStatus = 'rejected';
      else callHistoryStatus = 'missed';
      const callHistoryDuration = wasConnected && callConnectedAtRef.current
        ? Math.max(0, Math.round((Date.now() - callConnectedAtRef.current) / 1000))
        : 0;

      onEndCall({
        userId: targetUser.id,
        userName: targetUser.username || remoteName || 'Unknown User',
        type: 'video',
        direction: direction,
        status: callHistoryStatus,
        duration: callHistoryDuration,
        callId: endedCallId,
        isMultiParticipant: isMultiParticipantCallRef.current
      });
    }
    
    // Update UI state first
    setIsCallActive(false);
    setIsIncomingCall(false);
    setCallStatus('ended');
    stopCallTimer();

    // Stop any pending reconnect teardown timers
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    reconnectAttemptsRef.current = 0;
    
    // Cleanup media tracks
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        track.stop();
      });
    }
    
    if (remoteStreamRef.current) {
      remoteStreamRef.current.getTracks().forEach(track => {
        track.stop();
      });
    }
    
    // Clean video elements
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = null;
    }
    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null;
    }
    
    // Close peer connection
    const peerConnection = peerConnectionRef.current;
    if (peerConnection && peerConnection.signalingState !== 'closed') {
      try {
        peerConnection.close();
      } catch (err) {
        console.warn('PC close error:', err);
      }
    }

    // Close all additional peer connections
    additionalPeerConnectionsRef.current.forEach((pc, userId) => {
      try {
        if (pc && pc.signalingState !== 'closed') {
          pc.close();
        }
      } catch (err) {
        console.warn(`Error closing PC for ${userId}:`, err);
      }
    });
    additionalPeerConnectionsRef.current.clear();

    // Close any held 1:1 call too — and notify the held peer it's ended.
    const held = heldCallRef.current;
    if (held) {
      if (held.peerId) {
        try {
          socket.emit('videoCallEnded', { from: user.id, to: held.peerId, callId: held.callId });
        } catch (_) { /* best effort */ }
      }
      try {
        if (held.pc && held.pc.signalingState !== 'closed') held.pc.close();
      } catch (_) { /* best effort */ }
    }
    heldCallRef.current = null;
    setHasHeldCall(false);
    setHeldCallName('');
    setIsHeldByPeer(false);
    setWaitingCallSafe(null);
    
    // Reset all state
    setLocalStream(null);
    setRemoteStream(null);
    localStreamRef.current = null;
    remoteStreamRef.current = null;
    peerConnectionRef.current = null;
    setIsMuted(false);
    setIsVideoOff(false);
    setCallDuration(0);
    setCallerId(null);
    setCallerName('');
    setRemoteName('');
    setError(null);
    peerIdRef.current = null;
    callIdRef.current = null;
    pendingIceCandidates.current = [];
    pendingOffer.current = null;
    autoAcceptCall.current = false;
    // Reset call-history bookkeeping for the next call.
    callDirectionRef.current = null;
    callConnectedRef.current = false;
    callConnectedAtRef.current = null;
    callEndReasonRef.current = null;
    isMultiParticipantCallRef.current = false;
    multiCallInviteParticipantsRef.current.clear();
    callTargetRef.current = null;
    setAllParticipants(new Set());
    remoteStreamsRef.current.clear();
    remoteTileVideoElsRef.current.clear();
    setRemoteParticipantStreams([]);
    
    // Reset cleanup flag after a delay to allow new calls
    setTimeout(() => {
      cleanupDoneRef.current = false;
    }, 1000);
    
    console.log('✅ VideoCall: Cleanup complete');
  };

  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoOff(!videoTrack.enabled);
      }
    }
  };

  const flipCamera = async () => {
    if (!localStreamRef.current) return;
    const newMode = facingModeRef.current === 'user' ? 'environment' : 'user';
    const oldMode = facingModeRef.current;

    // Helper: wire new video track into the call
    const applyNewTrack = async (newTrack) => {
      const allPcs = [peerConnectionRef.current];
      additionalPeerConnectionsRef.current.forEach((pc) => allPcs.push(pc));
      for (const pc of allPcs) {
        if (!pc) continue;
        const sender = pc.getSenders().find(s => s?.track?.kind === 'video');
        if (sender?.replaceTrack) {
          await sender.replaceTrack(newTrack);
        }
      }
      localStreamRef.current.addTrack(newTrack);
      facingModeRef.current = newMode;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = localStreamRef.current;
      }
    };

    // CRITICAL: On mobile, stop old video track FIRST so hardware is released
    const oldTrack = localStreamRef.current.getVideoTracks()[0];
    const oldTrackSettings = oldTrack?.getSettings?.() || {};
    if (oldTrack) {
      localStreamRef.current.removeTrack(oldTrack);
      oldTrack.stop();
    }

    // Enumerate devices while camera is released
    let devices = [];
    try { devices = await navigator.mediaDevices.enumerateDevices(); } catch (_) {}
    const cameras = devices.filter(d => d.kind === 'videoinput');

    // Try to acquire the new camera
    const tryGetCamera = async (constraints) => {
      const stream = await navigator.mediaDevices.getUserMedia({ video: constraints, audio: false });
      const t = stream.getVideoTracks()[0];
      if (t) { await applyNewTrack(t); return true; }
      return false;
    };

    // Strategy 1: facingMode exact
    try {
      if (await tryGetCamera({ facingMode: { exact: newMode }, width: { ideal: 1280 }, height: { ideal: 720 } })) return;
    } catch (_) {}

    // Strategy 2: find by label
    if (cameras.length > 0) {
      let target = null;
      if (newMode === 'environment') {
        target = cameras.find(c => /back|rear|environment/i.test(c.label));
      } else {
        target = cameras.find(c => /front|user|facetime/i.test(c.label));
      }
      if (!target && cameras.length > 1) {
        target = cameras.find(c => c.deviceId !== oldTrackSettings.deviceId);
      }
      if (target) {
        try {
          if (await tryGetCamera({ deviceId: { exact: target.deviceId }, width: { ideal: 1280 }, height: { ideal: 720 } })) return;
        } catch (_) {}
      }
    }

    // Strategy 3: facingMode as hint
    try {
      if (await tryGetCamera({ facingMode: newMode, width: { ideal: 1280 }, height: { ideal: 720 } })) return;
    } catch (_) {}

    // All strategies failed — rollback: re-acquire original camera
    console.error('\u274c Failed to flip camera, rolling back to', oldMode);
    try {
      const rollback = await navigator.mediaDevices.getUserMedia({
        video: oldTrackSettings.deviceId
          ? { deviceId: { exact: oldTrackSettings.deviceId } }
          : { facingMode: oldMode },
        audio: false,
      });
      const t = rollback.getVideoTracks()[0];
      if (t) {
        const allPcs = [peerConnectionRef.current];
        additionalPeerConnectionsRef.current.forEach((pc) => allPcs.push(pc));
        for (const pc of allPcs) {
          if (!pc) continue;
          const sender = pc.getSenders().find(s => s?.track?.kind === 'video');
          if (sender?.replaceTrack) await sender.replaceTrack(t);
        }
        localStreamRef.current.addTrack(t);
        if (localVideoRef.current) localVideoRef.current.srcObject = localStreamRef.current;
      }
    } catch (rollbackErr) {
      console.error('\u274c Rollback also failed:', rollbackErr);
    }
    setError('Could not switch camera on this device');
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Generate or get call ID for multi-participant calls
  useEffect(() => {
    if (!callIdRef.current && (callStatus === 'connected' || callStatus === 'calling')) {
      callIdRef.current = `call-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }
  }, [callStatus]);

  // Fetch contacts for adding participants
  const fetchContacts = async () => {
    try {
      console.log('📞 Fetching contacts for adding participants...');
      console.log('📞 User ID:', user.id);
      
      const response = await axios.get(`${config.apiUrl}/api/contacts/${user.id}`);
      console.log('📞 Contacts API response:', response);
      console.log('📞 Contacts data:', response.data);
      
      if (!response.data || !Array.isArray(response.data)) {
        console.warn('⚠️ Invalid contacts data format:', response.data);
        setContacts([]);
        setError('No contacts found');
        return;
      }
      
      // Normalize contact data structure
      const normalizedContacts = response.data.map(contact => {
        console.log('📞 Processing contact:', contact);
        
        // Handle different contact structures
        const contactId = contact.id || contact._id?.toString() || contact.user?._id?.toString() || contact.user?.id?.toString();
        const username = contact.username || contact.user?.username || contact.recipient?.username || contact.requester?.username;
        const profileImage = contact.profileImage || contact.user?.profileImage || contact.recipient?.profileImage || contact.requester?.profileImage;
        
        const normalized = {
          id: contactId,
          username: username || contactId,
          profileImage: profileImage,
          _id: contactId
        };
        
        console.log('📞 Normalized contact:', normalized);
        return normalized;
      }).filter(contact => {
        const hasId = !!contact.id;
        if (!hasId) console.warn('⚠️ Skipping contact without ID:', contact);
        return hasId;
      });
      
      console.log('📞 Total normalized contacts:', normalizedContacts.length);

      // Keep contacts who are already in the call so we can show an "Already in call" badge.
      // Only exclude the current user themselves.
      const myId = user.id?.toString();
      const filtered = normalizedContacts.filter(contact => {
        const contactId = contact.id?.toString();
        return contactId && contactId !== myId;
      });

      console.log('📞 Filtered contacts count:', filtered.length);
      console.log('📞 Filtered contacts list:', filtered);

      setContacts(filtered);

      if (filtered.length === 0) {
        setError('No contacts available to invite');
      } else {
        setError(null);
      }
    } catch (error) {
      console.error('❌ Error fetching contacts:', error);
      console.error('❌ Error details:', error.response || error.message);
      setError('Failed to load contacts. Please try again.');
      setContacts([]);
    }
  };

  // Open add participants modal
  const handleAddParticipants = (e) => {
    console.log('📞 Opening add participants modal...');
    console.log('📞 Current showAddParticipants state:', showAddParticipants);
    console.log('📞 Current callStatus:', callStatus);
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setShowAddParticipants(true);
    console.log('📞 Set showAddParticipants to TRUE');
    setSelectedContacts(new Set());
    setParticipantSearchQuery('');
    setError(null);
    fetchContacts();
    
    // Force a check after state update
    setTimeout(() => {
      console.log('📞 After setState - showAddParticipants should be true');
      console.log('📞 Modal should now be visible in DOM');
    }, 100);
  };

  // Invite selected contacts to the call
  const getCurrentParticipantCount = () => {
    const ids = new Set();
    const myId = user.id?.toString();
    if (myId) ids.add(myId);
    const otherPeer = selectedUser?.id?.toString() || callerId?.toString();
    if (otherPeer) ids.add(otherPeer);
    allParticipants.forEach((id) => { if (id) ids.add(id.toString()); });
    additionalPeerConnectionsRef.current.forEach((_pc, id) => { if (id) ids.add(id.toString()); });
    return ids.size;
  };

  const handleInviteParticipants = async () => {
    if (selectedContacts.size === 0) {
      setError('Please select at least one contact to invite');
      return;
    }

    if (!socket || !localStreamRef.current) {
      setError('Call not ready for adding participants');
      return;
    }

    const currentCount = getCurrentParticipantCount();
    if (currentCount + selectedContacts.size > MAX_CALL_PARTICIPANTS) {
      const slotsLeft = Math.max(0, MAX_CALL_PARTICIPANTS - currentCount);
      setError(`Conference call limit is ${MAX_CALL_PARTICIPANTS} members. You can add ${slotsLeft} more participant${slotsLeft === 1 ? '' : 's'}.`);
      return;
    }

    try {
      const callId = callIdRef.current || `call-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      callIdRef.current = callId;

      // Get ALL existing participants (all current participants + user + other call participant)
      const currentParticipantsList = Array.from(allParticipants);
      const myId = user.id?.toString();
      const otherParticipant = selectedUser?.id?.toString() || callerId?.toString();
      
      // Ensure we include ourselves
      if (myId && !currentParticipantsList.includes(myId)) {
        currentParticipantsList.push(myId);
      }
      
      // Ensure we include the other call participant
      if (otherParticipant && !currentParticipantsList.includes(otherParticipant)) {
        currentParticipantsList.push(otherParticipant);
      }
      
      // Include all additional peer connections we already have
      additionalPeerConnectionsRef.current.forEach((pc, participantId) => {
        if (participantId && !currentParticipantsList.includes(participantId)) {
          currentParticipantsList.push(participantId);
        }
      });
      
      const existingParticipants = currentParticipantsList.filter(Boolean);
      
      console.log('📋 Complete list of existing participants for invite:', existingParticipants);

      console.log('📞 Inviting participants:', Array.from(selectedContacts));
      console.log('📞 Existing participants:', existingParticipants);
      console.log('📞 Call ID:', callId);

      // Mark as multi-participant and seed allParticipants with complete list
      isMultiParticipantCallRef.current = true;
      setAllParticipants(new Set(existingParticipants));

      // Seed the original 1:1 peer into the participant grid
      const originalPeerId = otherParticipant;
      if (originalPeerId && remoteStreamRef.current && !remoteStreamsRef.current.has(originalPeerId)) {
        upsertRemoteStream(originalPeerId, remoteStreamRef.current);
      }

      // Create offer for each selected contact
      const invitePromises = Array.from(selectedContacts).map(async (contactId) => {
        try {
          // Create peer connection with the new participant (always initiator when inviting)
          const pc = await createPeerConnectionWithParticipant(contactId, true);
          
          if (!pc) {
            throw new Error('Failed to create peer connection');
          }

          // Get caller name for notification
          const callerName = user.username || user.name || 'Someone';
          
          // Wait a bit for the offer to be created
          await new Promise(resolve => setTimeout(resolve, 100));
          
          const offer = pc.localDescription; // Offer was created in createPeerConnectionWithParticipant
          
          if (!offer) {
            throw new Error('No offer was created');
          }
          
          // Emit invite event
          socket.emit('multiVideoCallInvite', {
            from: user.id,
            to: contactId,
            offer: offer,
            callId: callId,
            existingParticipants: existingParticipants,
            callerName: callerName
          });

          // Track the new participant
          setAllParticipants(prev => {
            const newSet = new Set(prev);
            newSet.add(contactId);
            return newSet;
          });

          console.log(`✅ Invited ${contactId} to the call`);
          return { success: true, contactId };
        } catch (err) {
          console.error(`❌ Error inviting ${contactId}:`, err);
          return { success: false, contactId, error: err.message };
        }
      });

      // Wait for all invites to be sent
      const results = await Promise.all(invitePromises);
      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;

      if (successCount > 0) {
        console.log(`✅ Successfully invited ${successCount} participant(s)`);
        if (failCount > 0) {
          console.warn(`⚠️ Failed to invite ${failCount} participant(s)`);
        }
        setShowAddParticipants(false);
        setSelectedContacts(new Set());
        setError(null);
      } else {
        setError('Failed to invite participants. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error inviting participants:', error);
      setError('Failed to invite participants: ' + (error.message || 'Unknown error'));
    }
  };

  // Attach local stream to video element
  useEffect(() => {
    if (localVideoRef.current && localStreamRef.current) {
      localVideoRef.current.srcObject = localStreamRef.current;
      localVideoRef.current.muted = true;
      localVideoRef.current.play().catch(err => {
        console.warn('Local video play error:', err);
      });
    } else if (localVideoRef.current && !localStreamRef.current) {
      localVideoRef.current.srcObject = null;
    }
  }, [localStream, callStatus]);

  // Attach remote stream to video element
  useEffect(() => {
    const videoElement = remoteVideoRef.current;
    const stream = remoteStreamRef.current;
    
    if (videoElement && stream) {
      console.log('📹 Attaching remote stream to video element:', {
        tracksCount: stream.getTracks().length,
        videoTracks: stream.getVideoTracks().length,
        audioTracks: stream.getAudioTracks().length,
        callStatus: callStatus
      });
      
      // Check if stream has tracks
      if (stream.getTracks().length === 0) {
        console.warn('⚠️ Remote stream has no tracks');
        return;
      }
      
      // Set stream to video element
      videoElement.srcObject = stream;
      videoElement.muted = false;
      videoElement.autoplay = true;
      videoElement.playsInline = true;
      
      // Ensure all tracks are enabled
      stream.getTracks().forEach(track => {
        if (!track.enabled) {
          track.enabled = true;
          console.log('✅ Enabled', track.kind, 'track');
        }
      });
      
      // Play video
      const playPromise = videoElement.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('✅ Remote video playback started successfully');
          })
          .catch(err => {
            console.warn('⚠️ Remote video play error:', err);
            // Retry after a short delay
            setTimeout(() => {
              videoElement.play().catch(retryErr => {
                console.error('❌ Remote video play retry failed:', retryErr);
              });
            }, 500);
          });
      }
    } else if (videoElement && !stream) {
      console.log('📹 Clearing remote video element (no stream)');
      videoElement.srcObject = null;
    }
  }, [remoteStream, callStatus]);

  // Attach ALL remote participant streams to their grid video elements
  useEffect(() => {
    remoteParticipantStreams.forEach(({ userId, stream }) => {
      const el = remoteTileVideoElsRef.current.get(userId);
      if (!el || !stream) return;
      if (el.srcObject !== stream) {
        el.srcObject = stream;
      }
      el.muted = false;
      el.autoplay = true;
      el.playsInline = true;
      const p = el.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    });
  }, [remoteParticipantStreams]);

  // End this video call if an audio or group call is accepted.
  useEffect(() => {
    const onOtherCallAccepted = (e) => {
      const accepted = e.detail?.acceptedType;
      if (
        (accepted === 'audio' || accepted === 'group') &&
        callStatusRef.current !== 'idle' &&
        callStatusRef.current !== 'ended'
      ) {
        console.log('🔄 VideoCall: ending because another call was accepted:', accepted);
        endCall();
      }
    };
    window.addEventListener('endOtherCallTypes', onOtherCallAccepted);
    return () => window.removeEventListener('endOtherCallTypes', onOtherCallAccepted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return registerCallSession({
      id: sessionIdRef.current,
      kind: 'direct',
      callType: 'video',
      isActive: () => {
        const s = callStatusRef.current;
        return s === 'incoming' || s === 'calling' || s === 'connecting' || s === 'connected';
      },
      leave: () => endCallRef.current?.(),
    });
  }, []);

  // Listen for outgoing-call requests dispatched by the chat-header button
  // (Home.js). VideoCall is mounted exactly once per app (in App.js); the
  // chat-header just publishes "please call this user" and the global instance
  // handles the entire call lifecycle. This prevents duplicate socket
  // listeners from showing the incoming-call modal twice on the receiver.
  useEffect(() => {
    const onRequestStart = (e) => {
      const target = e.detail?.user;
      if (!target) return;
      if (callStatusRef.current !== 'idle' && callStatusRef.current !== 'ended') {
        console.warn('⚠️ VideoCall: ignoring requestStartVideoCall — already in call', callStatusRef.current);
        return;
      }
      startCall(target);
    };
    window.addEventListener('requestStartVideoCall', onRequestStart);
    return () => window.removeEventListener('requestStartVideoCall', onRequestStart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildVideoSnapshot = () => {
    const status = callStatusRef.current;
    if (!['calling', 'connecting', 'connected'].includes(status)) return null;
    const peerId = (callTargetRef.current?.id || peerIdRef.current || callerId)?.toString();
    if (!peerId) return null;
    if (!callIdRef.current) {
      callIdRef.current = `call-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }
    return {
      kind: 'video',
      userId: user?.id?.toString(),
      callId: callIdRef.current,
      status,
      peer: {
        id: peerId,
        username: callTargetRef.current?.username || remoteName || callerName || '',
      },
      participants: Array.from(allParticipantsRef.current || []),
      isMulti: !!isMultiParticipantCallRef.current,
      direction: callDirectionRef.current,
      connectedAt: callConnectedAtRef.current,
      isMuted,
      isVideoOff,
    };
  };

  useEffect(() => {
    allParticipantsRef.current = allParticipants;
  }, [allParticipants]);

  useEffect(() => {
    const active = ['calling', 'connecting', 'connected'].includes(callStatus);
    if (active) {
      hadLiveCallRef.current = true;
      const snapshot = buildVideoSnapshot();
      if (snapshot) saveActiveCall(snapshot);
      return;
    }
    if (hadLiveCallRef.current && !isPageUnloading()) {
      hadLiveCallRef.current = false;
      clearActiveCall('video');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callStatus, isMuted, isVideoOff, remoteName, callerName, allParticipants]);

  buildVideoSnapshotRef.current = buildVideoSnapshot;

  useEffect(() => {
    const flush = () => {
      const snapshot = buildVideoSnapshotRef.current?.();
      if (snapshot) saveActiveCall(snapshot);
    };
    window.addEventListener('beforeunload', flush);
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('beforeunload', flush);
      window.removeEventListener('pagehide', flush);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After a refresh, join the same call again instead of dropping it.
  useEffect(() => {
    if (!socket || !user?.id || resumeAttemptedRef.current) return undefined;
    const preview = readActiveCall();
    if (!preview || preview.kind !== 'video') return undefined;
    if (preview.userId && preview.userId !== user.id.toString()) return undefined;
    resumeAttemptedRef.current = true;
    const snapshot = consumeActiveCall('video', user.id);
    if (!snapshot?.peer?.id) return undefined;

    let cancelled = false;
    (async () => {
      const ready = await waitForSocket(socket);
      if (cancelled || !ready) {
        if (!cancelled && !ready) setError('Could not reconnect to the call');
        return;
      }
      callIdRef.current = snapshot.callId || callIdRef.current;
      callDirectionRef.current = snapshot.direction || 'outgoing';
      peerIdRef.current = snapshot.peer.id;
      callTargetRef.current = snapshot.peer;
      setRemoteName(snapshot.peer.username || '');
      setCallerName(snapshot.peer.username || '');
      if (snapshot.connectedAt) {
        callConnectedRef.current = true;
        callConnectedAtRef.current = snapshot.connectedAt;
      }
      const others = (snapshot.participants || [])
        .map((id) => id?.toString())
        .filter((id) => id && id !== user.id.toString());
      const resumeMulti = !!snapshot.isMulti && others.length > 0;
      if (!resumeMulti) {
        await startCall(snapshot.peer, {
          resume: !!snapshot.connectedAt,
          wasConnected: !!snapshot.connectedAt,
          callId: snapshot.callId,
          isMuted: snapshot.isMuted,
          isVideoOff: snapshot.isVideoOff,
        });
        return;
      }

      isMultiParticipantCallRef.current = true;
      setAllParticipants(new Set([user.id.toString(), snapshot.peer.id.toString(), ...others]));
      setIsCallActive(true);
      setCallStatus(snapshot.connectedAt ? 'connecting' : 'calling');
      setError('Reconnecting…');
      try {
        const media = await acquireLocalMedia({ allowReuse: true });
        const stream = media.stream;
        if (snapshot.isMuted) stream.getAudioTracks().forEach((track) => { track.enabled = false; });
        if (snapshot.isVideoOff) stream.getVideoTracks().forEach((track) => { track.enabled = false; });
        setIsMuted(!!snapshot.isMuted);
        setIsVideoOff(!!snapshot.isVideoOff);
        setLocalStream(stream);
        localStreamRef.current = stream;

        const rejoin = await new Promise((resolve) => {
          socket.emit('multiVideoCallRejoin', {
            from: user.id,
            callId: snapshot.callId,
            existingParticipants: [user.id.toString(), snapshot.peer.id.toString(), ...others],
          }, (ack) => resolve(ack || { ok: false }));
        });
        if (!rejoin?.ok) {
          setError('The call has ended');
          handleEndCall();
          return;
        }
        const roster = (rejoin.participants || others).map((id) => id?.toString()).filter(Boolean);
        sendingResumeOffersRef.current = true;
        try {
          for (const pid of roster) {
            if (!pid || pid === user.id.toString()) continue;
            await createPeerConnectionWithParticipant(pid, true);
          }
        } finally {
          sendingResumeOffersRef.current = false;
        }
        if (snapshot.connectedAt) startCallTimer();
        setError(null);
      } catch (err) {
        console.error('Failed to rejoin video call:', err);
        setError('Could not reconnect to the call');
        handleEndCall();
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, user?.id]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      handleEndCall();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isIncomingCall) {
    return createPortal(
      (<div className="video-call-overlay">
        <div className="video-call-modal incoming">
          <div className="call-header">
            <h3>Incoming Video Call</h3>
            <p>{callerName || callerId || 'Unknown'}</p>
          </div>
          {error && <div className="call-error">{error}</div>}
          <div className="call-actions">
            <button className="accept-btn" onClick={acceptCall}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
              </svg>
              Accept
            </button>
            <button className="reject-btn" onClick={rejectCall}>
              <EndCallIcon size={24} />
              Reject
            </button>
          </div>
        </div>
      </div>),
      document.body
    );
  }

  if (callStatus === 'calling') {
    return createPortal(
      (<div className="video-call-overlay">
        <div className="video-call-modal calling">
          <div className="call-header">
            <h3>Calling...</h3>
            <p>{remoteName || selectedUser?.username || 'Unknown'}</p>
          </div>
          {error && <div className="call-error">{error}</div>}
          <div className="call-status">
            <div className="calling-animation">
              <div className="pulse"></div>
              <div className="pulse"></div>
              <div className="pulse"></div>
            </div>
          </div>
          <div className="call-actions">
            <button className="end-call-btn" onClick={endCall}>
              <EndCallIcon size={24} />
              End Call
            </button>
          </div>
        </div>
      </div>),
      document.body
    );
  }

  if ((callStatus === 'connected' || isCallActive) && callStatus !== 'ended') {
    return (
      <>
        {createPortal(
          <div className="video-call-overlay" style={{ cursor: 'default' }}>
            <div className="video-call-container">
          {/* Video Grid — all participants share screen equally */}
          <div
            className="video-grid"
            data-count={remoteParticipantStreams.length <= 1 ? 2 : remoteParticipantStreams.length + 1}
          >
            {/* Remote Videos */}
            {remoteParticipantStreams.length <= 1 ? (
              <div className={`video-grid-item${isHeldByPeer ? ' is-on-hold' : ''}`}>
                <video
                  ref={remoteVideoRef}
                  autoPlay
                  playsInline
                  aria-label="Remote video feed"
                />
                {isHeldByPeer && (
                  <div className="video-tile-hold-badge" aria-label="On hold">
                    <span className="video-tile-hold-dot" aria-hidden="true" />
                    <span>On hold</span>
                  </div>
                )}
                <span className="participant-label">
                  {remoteName || selectedUser?.username || callerName || 'Remote'}
                </span>
              </div>
            ) : (
              remoteParticipantStreams.map(({ userId }) => (
                <div key={userId} className="video-grid-item">
                  <video
                    ref={(el) => {
                      if (el) remoteTileVideoElsRef.current.set(userId, el);
                      else remoteTileVideoElsRef.current.delete(userId);
                    }}
                    autoPlay
                    playsInline
                    muted={false}
                    aria-label={`Remote video ${userId}`}
                  />
                  <span className="participant-label">{getParticipantDisplayName(userId)}</span>
                </div>
              ))
            )}
            {/* Local Video — equal size in grid */}
            <div className="video-grid-item local-tile">
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                aria-label="Your video"
              />
              <span className="participant-label">{getParticipantDisplayName(user?.id)}</span>
            </div>
          </div>

          {/* Call Controls */}
          <div className="video-call-controls">
            <button 
              className={`control-btn ${isMuted ? 'active' : ''}`} 
              onClick={toggleMute}
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              <MuteIcon muted={isMuted} size={20} />
            </button>

            <button 
              className={`control-btn ${isVideoOff ? 'active' : ''}`} 
              onClick={toggleVideo}
              title={isVideoOff ? 'Turn on video' : 'Turn off video'}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {isVideoOff ? (
                  <>
                    <path d="M1 1l22 22"/>
                    <path d="M16 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4"/>
                    <path d="M8 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4"/>
                  </>
                ) : (
                  <>
                    <polygon points="23 7 16 12 23 17 23 7"/>
                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                  </>
                )}
              </svg>
            </button>

            {!isVideoOff && (
              <button
                className="control-btn"
                onClick={flipCamera}
                title="Flip camera"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  <path d="M15.5 12.5a3.5 3.5 0 0 1-5.8 2.2" />
                  <path d="M9.5 14.5l-1.2 1.2" />
                  <path d="M8.5 12.5a3.5 3.5 0 0 1 5.8-2.2" />
                  <path d="M14.5 10.5l1.2-1.2" />
                </svg>
              </button>
            )}

            {/* Add participants button hidden for individual calls
            <button 
              className="control-btn" 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('📞 Add Participants button clicked');
                handleAddParticipants();
              }}
              title="Add participants"
              type="button"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="8.5" cy="7" r="4"/>
                <line x1="20" y1="8" x2="20" y2="14"/>
                <line x1="23" y1="11" x2="17" y2="11"/>
              </svg>
            </button>
            */}

            <button className="control-btn end-call" onClick={endCall} title="End call">
              <EndCallIcon size={20} />
            </button>
          </div>

          {/* Call Info */}
          <div className="call-info">
            <h3>{remoteName || selectedUser?.username || selectedUser?.id || callerName || 'Unknown'}</h3>
            <span className="call-duration">{formatDuration(callDuration)}</span>
            {isHeldByPeer && (
              <span className="call-info-hold-pill" title="You are on hold">
                <span className="call-info-hold-dot" aria-hidden="true" /> On hold
              </span>
            )}
            {hasHeldCall && (
              <span className="call-info-hold-pill alt" title={`${heldCallName || 'Other call'} is on hold`}>
                <span className="call-info-hold-dot" aria-hidden="true" /> {heldCallName || 'Other'} on hold
              </span>
            )}
          </div>
        </div>
      </div>,
      document.body
        )}

        {/* Add Participants Modal - Separate Portal */}
        {(console.log('📞 Modal render check: showAddParticipants =', showAddParticipants, 'contacts:', contacts.length), showAddParticipants) && (
          console.log('📞 ✅ RENDERING MODAL PORTAL NOW!'),
          createPortal(
            <div 
              className="modal-overlay" 
              style={{ display: 'flex' }}
              onClick={() => {
                console.log('📞 Closing modal from overlay click');
                setShowAddParticipants(false);
              }}
            >
              <div className="modal-content" onClick={(e) => {
                console.log('📞 Modal content clicked - preventing close');
                e.stopPropagation();
              }}>
              <div className="modal-header">
                <h2>Add Participants</h2>
                <button className="close-btn" onClick={() => {
                  setShowAddParticipants(false);
                  setParticipantSearchQuery('');
                }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
              <div className="modal-body">
                <div className="contacts-search-wrapper">
                  <svg className="contacts-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="7"/>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <input
                    type="text"
                    className="contacts-search-input"
                    placeholder="Search contacts..."
                    value={participantSearchQuery}
                    onChange={(e) => setParticipantSearchQuery(e.target.value)}
                    autoFocus
                  />
                  {participantSearchQuery && (
                    <button
                      type="button"
                      className="contacts-search-clear"
                      onClick={() => setParticipantSearchQuery('')}
                      aria-label="Clear search"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  )}
                </div>
                {error && (
                  <div className="call-error" style={{ marginBottom: '16px' }}>
                    {error}
                  </div>
                )}
                {(() => {
                  const inCallIds = new Set();
                  const otherPeer = selectedUser?.id?.toString() || callerId?.toString();
                  if (otherPeer) inCallIds.add(otherPeer);
                  allParticipants.forEach((id) => { if (id) inCallIds.add(id.toString()); });
                  additionalPeerConnectionsRef.current.forEach((_pc, id) => {
                    if (id) inCallIds.add(id.toString());
                  });
                  const myId = user.id?.toString();
                  if (myId) inCallIds.delete(myId);

                  const query = participantSearchQuery.trim().toLowerCase();
                  const visibleContacts = contacts.filter((contact) => {
                    if (!query) return true;
                    const username = (contact.username || '').toLowerCase();
                    return username.includes(query);
                  });

                  if (contacts.length === 0) {
                    return (
                      <p style={{ textAlign: 'center', padding: '20px', color: '#54656f' }}>
                        {error ? error : 'No contacts available to invite'}
                      </p>
                    );
                  }

                  if (visibleContacts.length === 0) {
                    return (
                      <p style={{ textAlign: 'center', padding: '20px', color: '#54656f' }}>
                        No contacts match &ldquo;{participantSearchQuery}&rdquo;
                      </p>
                    );
                  }

                  const currentCount = inCallIds.size + 1; // +1 for self
                  const slotsLeft = Math.max(0, MAX_CALL_PARTICIPANTS - currentCount);
                  const selectionLimitReached = selectedContacts.size >= slotsLeft;

                  return (
                    <>
                      {selectionLimitReached && slotsLeft > 0 && (
                        <p style={{ textAlign: 'center', padding: '8px 12px', color: '#e65100', fontSize: '13px', background: '#fff3e0', borderRadius: '8px', margin: '0 0 8px' }}>
                          Conference call limit is {MAX_CALL_PARTICIPANTS} members. You can add {slotsLeft} more.
                        </p>
                      )}
                      {slotsLeft === 0 && (
                        <p style={{ textAlign: 'center', padding: '8px 12px', color: '#c62828', fontSize: '13px', background: '#ffebee', borderRadius: '8px', margin: '0 0 8px' }}>
                          Conference call limit reached ({MAX_CALL_PARTICIPANTS} members). Cannot add more participants.
                        </p>
                      )}
                    <div className="contacts-list">
                      {visibleContacts.map((contact) => {
                        const contactId = contact.id || contact._id?.toString();
                        if (!contactId) return null;
                        const username = contact.username || contactId;
                        const profileImage = contact.profileImage;
                        const alreadyInCall = inCallIds.has(contactId.toString());
                        const isSelected = !alreadyInCall && selectedContacts.has(contactId);
                        const isDisabledByLimit = !alreadyInCall && !isSelected && (selectionLimitReached || slotsLeft === 0);

                        return (
                          <div
                            key={contactId}
                            className={`contact-item ${isSelected ? 'selected' : ''} ${alreadyInCall ? 'in-call' : ''} ${isDisabledByLimit ? 'disabled' : ''}`}
                            aria-disabled={alreadyInCall || isDisabledByLimit}
                            onClick={() => {
                              if (alreadyInCall || isDisabledByLimit) return;
                              const newSelected = new Set(selectedContacts);
                              if (isSelected) {
                                newSelected.delete(contactId);
                              } else {
                                newSelected.add(contactId);
                              }
                              setSelectedContacts(newSelected);
                              console.log('📞 Selected contacts:', Array.from(newSelected));
                            }}
                          >
                            <div className="contact-avatar">
                              {profileImage ? (
                                <img src={`${config.apiUrl}${profileImage}`} alt={username} onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }} />
                              ) : null}
                              <div className="avatar-placeholder" style={{ display: profileImage ? 'none' : 'flex' }}>
                                {username?.charAt(0).toUpperCase() || '?'}
                              </div>
                            </div>
                            <div className="contact-info">
                              <span className="contact-name">{username}</span>
                              {alreadyInCall && (
                                <span className="contact-substatus">In this call</span>
                              )}
                            </div>
                            {alreadyInCall ? (
                              <div className="in-call-badge" title="This contact is already in the call">
                                Already in call
                              </div>
                            ) : isSelected ? (
                              <div className="checkmark">✓</div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                    </>
                  );
                })()}
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => {
                  setShowAddParticipants(false);
                  setSelectedContacts(new Set());
                  setParticipantSearchQuery('');
                }}>
                  Cancel
                </button>
                <button 
                  className="btn-primary" 
                  onClick={handleInviteParticipants}
                  disabled={selectedContacts.size === 0}
                >
                  Invite {selectedContacts.size > 0 ? `(${selectedContacts.size})` : ''}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
        )}
        {waitingCall && createPortal(
          <div className="call-waiting-banner" role="alertdialog" aria-label="Incoming call while busy">
            <div className="call-waiting-info">
              <div className="call-waiting-title">Incoming video call</div>
              <div className="call-waiting-name">{waitingCall.fromName || waitingCall.from}</div>
            </div>
            <div className="call-waiting-actions">
              <button
                type="button"
                className="accept-btn"
                onClick={acceptWaitingCall}
                title="Hold current call and answer"
              >
                Accept
              </button>
              <button
                type="button"
                className="reject-btn"
                onClick={rejectWaitingCall}
                title="Decline"
              >
                Reject
              </button>
            </div>
          </div>,
          document.body
        )}
        {hasHeldCall && createPortal(
          <div className="call-on-hold-chip" role="status" aria-label="Call on hold">
            <span className="call-on-hold-dot" aria-hidden="true" />
            <span className="call-on-hold-text">
              <span className="call-on-hold-label">On hold</span>
              <span className="call-on-hold-name">{heldCallName || 'Other call'}</span>
            </span>
            <button type="button" className="call-on-hold-swap" onClick={swapHeldCall} title="Resume this call (current call goes on hold)">
              Swap
            </button>
            <button type="button" className="call-on-hold-end" onClick={endHeldCall} title="End the held call">
              End
            </button>
          </div>,
          document.body
        )}
        {isHeldByPeer && createPortal(
          <div className="call-held-by-peer-overlay" role="status" aria-label="You are on hold">
            <div className="call-held-by-peer-card">
              <div className="call-held-by-peer-title">On hold</div>
              <div className="call-held-by-peer-sub">{remoteName || callerName || 'The other party'} put you on hold</div>
            </div>
          </div>,
          document.body
        )}
      </>
    );
  }

  // When mounted globally without a selectedUser (App.js does this so the
  // component can listen for incoming calls anywhere in the app), don't render
  // a stray disabled button. The chat-header in Home.js renders its own
  // trigger button that dispatches `requestStartVideoCall`.
  if (!selectedUser) return null;

  return (
    <span
      className={`action-icon${!selectedUser ? ' disabled' : ''}`}
      onClick={selectedUser ? startCall : undefined}
      title={selectedUser ? 'Start video call' : 'Select a user to call'}
      role="button"
      aria-label="Start video call"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && selectedUser) startCall();
      }}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polygon points="23 7 16 12 23 17 23 7"/>
        <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
      </svg>
    </span>
  );
};

export default VideoCall;
